/** 产品模板编辑页（独立页面，不复用发布产品页组件） */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeOptions, optionLabel } from '@/i18n/chartText';
import {
  OUTER_SHAPE_KEYS,
  OUTER_TYPE_KEYS,
  PACKED_KEYS,
  PIECE_UNIT_KEYS,
  SENSITIVE_KEYS,
  SKU_CLASS_KEYS,
  YES_NO_KEYS,
} from '@/i18n/pkgOptionKeys';
import { fixedUi } from '@/i18n/fixedUi';
import { labeledSiteOptions } from '@/i18n/siteOptions';
import { useNavigate } from 'react-router-dom';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import AttributeField from '@/tiktok-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/tiktok-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/tiktok-platform/modules/products/components/SearchableSelect';
import SpecSupplySection from '@/tiktok-platform/modules/products/components/SpecSupplySection';
import TiktokDateTimePicker from '@/tiktok-platform/modules/products/components/TiktokDateTimePicker';
import TiktokOfficialSizeChartPanel, {
  fetchSizeChartClassRequired,
} from '@/tiktok-platform/modules/products/components/TiktokOfficialSizeChartPanel';
import TiktokComplianceRulePanel from '@/tiktok-platform/modules/products/components/TiktokComplianceRulePanel';
import TitleFieldLoadingShell from '@/tiktok-platform/modules/products/components/TitleFieldLoadingShell';
import TiktokDescriptionRichEditor from '@/tiktok-platform/modules/products/components/TiktokDescriptionRichEditor';
import TiktokModelSelectBar from '@/tiktok-platform/modules/products/components/TiktokModelSelectBar';
import TiktokGuideFilePanel from '@/tiktok-platform/modules/products/components/TiktokGuideFilePanel';
import FieldHelpMark from '@/components/FieldHelpMark';
import PublishScrollJumpFab from '@/components/publish/PublishScrollJumpFab';
import {
  buildTiktokModelTryOnSizeOptions,
  officialSizeChartDraftSignature,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import PackageBatchModal from '@/tiktok-platform/modules/products/components/PackageBatchModal';
import {
  TIKTOK_OUTER_PACKAGE_SHAPE_OPTIONS,
  TIKTOK_OUTER_PACKAGE_TYPE_OPTIONS,
  coerceOuterPackageFormFields,
} from '@/tiktok-platform/utils/tiktokOuterPackage';
import PublishImageUploadZone from '@/tiktok-platform/modules/products/components/PublishImageUploadZone';
import {
  TIKTOK_DIM_UNIT,
  TIKTOK_INDIVIDUALLY_PACKED_OPTIONS,
  TIKTOK_PIECE_UNIT_OPTIONS,
  TIKTOK_SENSITIVE_LIST_OPTIONS,
  TIKTOK_SENSITIVE_YES_NO_OPTIONS,
  TIKTOK_SKU_CLASSIFICATION_OPTIONS,
  TIKTOK_WEIGHT_UNIT,
  sensitiveListFromSlots,
  sensitiveSlotsFromList,
} from '@/tiktok-platform/utils/tiktokSkuPackage';
import {
  applyPackageBatchBySize,
  applyPackageBatchToAllSkus,
  firstFilledSkuPackageField,
} from '@/tiktok-platform/utils/tiktokPackageBatch';
import { resolveTiktokProductLevelImages } from '@/tiktok-platform/utils/tiktokProductImage';
import TiktokSwitch from '@/tiktok-platform/components/TiktokSwitch';
import SaveAttributeTemplateMetaModal from '@/tiktok-platform/modules/templates/SaveAttributeTemplateMetaModal';
import { buildTiktokPath, openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import { isOfficialProductTemplateScope } from '@/tiktok-platform/constants/tiktokProductTemplateScope';
import { useTiktokShopOptional } from '@/tiktok-platform/context/TiktokShopContext';
import { flattenLeafCategories } from '@/tiktok-platform/utils/tiktokCategoryTree';
import { loadShopCategoriesCached } from '@/tiktok-platform/utils/tiktokTemplateScopeCache';
import { hideTiktokPublishField, isTiktokTtsPublishMode } from '@/tiktok-platform/utils/tiktokPublishUiMode';
import {
  calcPublishCompletion,
  parseAttributeTemplate,
  partitionProductAttrsForDisplay,
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { applyShopBrandsToAttrTemplate, shopBrandsToSelectOptions } from '@/tiktok-platform/utils/tiktokShopBrandAttrs';
import { layoutAttributeFields } from '@/tiktok-platform/utils/tiktokAttributeLayout';
import {
  isComplianceProductAttr,
  splitComplianceProductAttrs,
} from '@/tiktok-platform/utils/tiktokComplianceAttrs';
import {
  EMPTY_TIKTOK_CATEGORY_RULES,
  normalizeTiktokCategoryRules,
} from '@/tiktok-platform/utils/tiktokCategoryRules';
import {
  isEditorAttrRequiredMark,
  isEditorAttrVisible,
  pruneInvisibleProductAttributes,
} from '@/tiktok-platform/utils/tiktokAttrVisibility';
import { parseCustomAttributePermission } from '@/tiktok-platform/utils/tiktokCustomAttribute';
import { filterTiktokSalesWarehouses } from '@/tiktok-platform/utils/tiktokSalesWarehouse';
import {
  applyProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
  filterProductAttributesToCategory,
  pickFilledProductAttributes,
  remapProductAttributesAcrossCategories,
  remapSizeChartAcrossCategories,
  remapSkcListAcrossCategories,
} from '@/tiktok-platform/utils/tiktokProductAttributeTemplate';
import { writeProductTemplatePublishHandoff } from '@/tiktok-platform/utils/tiktokProductTemplatePublishHandoff';
import {
  enrichTiktokFormPrintNames,
  printNamesFromTiktokPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToTiktokPublishForm';
import { pickAutoRuleTemplate } from '@/tiktok-platform/utils/tiktokTemplateAutoRule';
import { nextTemplatePriority } from '@/tiktok-platform/utils/tiktokTemplatePriority';
import { styleNoFromSupplierCode } from '@/tiktok-platform/utils/tiktokStyleNo';
import {
  checkTemplateVersions,
  collectAppliedTemplateRefs,
  conflictFieldKey,
  conflictToastMessage,
  extractAttrTemplatePatch,
  extractSizeChartTemplatePatch,
  isMissingTemplateError,
  isTemplateVersionConflictError,
  parseTemplateVersion,
  partitionTemplateVersionConflicts,
  TEMPLATE_VERSION_TYPES,
} from '@/tiktok-platform/utils/tiktokTemplateVersionGuard';
import {
  diffProductAttributeChanges,
  diffSizeChartChanges,
  indexAttributeChangeHints,
} from '@/tiktok-platform/utils/tiktokTemplateChangeDiff';
import TemplateVersionConflictMark from '@/tiktok-platform/modules/products/components/TemplateVersionConflictMark';
import TemplateChangeDiffList from '@/tiktok-platform/modules/products/components/TemplateChangeDiffList';
import {
  customLabelFromKey,
  isCustomSpecKey,
  skuSizeKey,
} from '@/tiktok-platform/utils/tiktokSizeChart';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  pickPublishCurrency,
  resolveSkuPackageCapabilities,
} from '@/tiktok-platform/utils/tiktokPublishFillInStandard';
import { TIKTOK_MODE_SEMI, tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import { loadShopPublishCurrencies } from '@/tiktok-platform/utils/resolveTiktokPublishCurrency';
import { listEnabledSites, pickSiteListCurrency } from '@/tiktok-platform/utils/tiktokSiteList';
import {
  persistTiktokFormImageSlots,
  sanitizeTiktokFormImageSlots,
} from '@/tiktok-platform/utils/sanitizeTiktokFormImageSlots';
import { normalizeTiktokAttrsToEditorShape } from '@/tiktok-platform/utils/normalizeTiktokAttrsForEditor';
import {
  generateTiktokPublishAiDescription,
  generateTiktokPublishAiTitle,
  resolveAllSkcMainImageUrls,
  translateTiktokTitleToEnglish,
} from '@/tiktok-platform/utils/tiktokPublishAiTitle';
import {
  parseTiktokStringList,
  resolveTiktokPreSaleRule,
  resolveTiktokPreSaleTypes,
  tiktokMultilineText,
  tiktokStringListChars,
  TIKTOK_DESCRIPTION_MAX_CHARS,
  TIKTOK_EXTERNAL_PRODUCT_ID_MAX,
  TIKTOK_KEY_FEATURES_MAX,
  TIKTOK_KEY_FEATURES_MAX_CHARS,
  TIKTOK_SEARCH_TERMS_MAX,
  TIKTOK_SEARCH_TERMS_MAX_CHARS,
  TIKTOK_TITLE_MAX_CHARS_DEFAULT,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import { buildCreditActionHint } from '@/constants/ipeCredits';
import { confirmIpeCreditAction } from '@/utils/ipeCreditConfirm';
import { tiktokPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';

function fetchAttrTemplateDetail(officialMode, shopId, id, extra = {}) {
  if (officialMode) {
    return adminAPI.getOfficialTiktokAttributeTemplate(id);
  }
  return tiktokPlatformAPI.getProductAttributeTemplate(shopId, id, extra);
}

function fetchAttrTemplateList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialTiktokAttributeTemplates(params);
  }
  return tiktokPlatformAPI.listProductAttributeTemplates(shopId, params);
}

function fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, extra = {}) {
  const body = { product_type_id_list: [Number(productTypeId)] };
  const cat = Number(categoryId);
  if (Number.isFinite(cat) && cat > 0) body.category_id = cat;
  if (officialMode) {
    if (extra.shopType) body.shopType = extra.shopType;
    if (extra.categoryPathLabel) body.categoryPathLabel = extra.categoryPathLabel;
    const catId = Number.isFinite(cat) && cat > 0 ? cat : Number(productTypeId);
    return adminAPI.getOfficialTiktokCatalogAttributeTemplate(body).then((raw) => (
      normalizeTiktokAttrsToEditorShape(raw, { catId })
    ));
  }
  return tiktokPlatformAPI.getAttributeTemplate(shopId, body, { forceRefresh: true });
}

function fetchSizeChartTemplateDetail(officialMode, shopId, id, extra = {}) {
  if (officialMode) {
    return adminAPI.getOfficialTiktokSizeChartTemplate(id);
  }
  return tiktokPlatformAPI.getSizeChartTemplate(shopId, id, extra);
}

function FieldErrorText({ message }) {
  if (!message) return null;
  return <div className="tko-field__error" role="alert">{message}</div>;
}

/** 模板编辑无店铺市场元数据时，用 Create Product 默认上限 255（非 Temu 250） */
const TITLE_MAX_CHARS = TIKTOK_TITLE_MAX_CHARS_DEFAULT;

/** 品牌下拉单页条数：品牌库可能有上百条，其余靠远程模糊搜索 */
const BRAND_DROPDOWN_PAGE_SIZE = 100;

/** 预售类型（pre_sale.type）展示名；仅类目规则允许时可选 */
const TIKTOK_PRESALE_TYPE_KEYS = {
  PRE_ORDER: 'preOrder',
  MADE_TO_ORDER: 'madeToOrder',
  CUSTOM: 'custom',
};

/** 承诺发货时效（秒） */
const SHIPMENT_LIMIT_OPTIONS = [
  { value: '86400', labelKey: 'ship1' },
  { value: '172800', labelKey: 'ship2' },
  { value: '259200', labelKey: 'ship3' },
  { value: '432000', labelKey: 'ship5' },
  { value: '604800', labelKey: 'ship7' },
];

/** 半托管常用站点（文档枚举，value=siteId）。编号与 TEMU 不是同一套。 */
const TIKTOK_SEMI_SITES = [
  ['100', '美国站'],
  ['101', '加拿大站'],
  ['102', '英国站'],
  ['103', '澳大利亚站'],
  ['104', '新西兰站'],
  ['105', '德国站'],
  ['106', '法国站'],
  ['107', '意大利站'],
  ['108', '西班牙站'],
  ['109', '日本站'],
  ['112', '墨西哥站'],
  ['118', '巴西站'],
  ['120', '韩国站'],
];

function imageUrlOfSlot(image) {
  if (!image) return '';
  return String(
    image.tiktokUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

const SECTIONS = [
  { id: 'basic', title: '基础信息' },
  { id: 'attrs', title: '属性' },
  { id: 'compliance', title: '商品合规' },
  { id: 'spec', title: '规格供应' },
  { id: 'size', title: '包装信息' },
  { id: 'sales', title: '销售发货' },
];

const SECTION_TITLE_KEYS = {
  basic: 'tiktokPublish.sectionBasic',
  attrs: 'tiktokPublish.sectionAttrs',
  compliance: 'tiktokPublish.sectionCompliance',
  spec: 'tiktokPublish.sectionSpec',
  size: 'tiktokPublish.sectionSize',
  sales: 'tiktokPublish.salesTitle',
};

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function parseSizeSelectionKey(sizeId) {
  const key = String(sizeId || '');
  if (isCustomSpecKey(key)) {
    const label = customLabelFromKey(key);
    return {
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label,
    };
  }
  return {
    sizeValueId: key,
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
  };
}

function buildSkuRowsForSizes(existingRows = [], sizeIds = [], sizeAttr, defaults = {}) {
  if (!sizeIds.length) {
    // 编辑回填：已有多行 SKU 时绝不能压成一行（模板过滤尺码后 sizeIds 可能暂时为空）
    if (existingRows.length > 1) {
      return existingRows.map((row) => ({
        ...row,
        sizeAttributeId: sizeAttr?.attributeId || row.sizeAttributeId || '',
      }));
    }
    // 未选尺码时保留一行空 SKU，便于仅按颜色填价库存
    const first = existingRows[0];
    return [first
      ? {
        ...first,
        sizeValueId: '',
        sizeIsCustom: false,
        sizeExtraValue: '',
        sizeLabel: '',
        sizeAttributeId: sizeAttr?.attributeId || first.sizeAttributeId || '',
      }
      : createSkuRow({
        sizeAttributeId: sizeAttr?.attributeId || '',
        price: defaults.price || '',
        stock: defaults.stock || '',
        weight: defaults.weight || '',
        netWeight: defaults.netWeight || '',
        currency: defaults.currency || '',
      })];
  }
  return sizeIds.map((sizeId) => {
    const parsed = parseSizeSelectionKey(sizeId);
    const found = existingRows.find((row) => skuSizeKey(row) === String(sizeId));
    if (found) {
      return {
        ...found,
        sizeAttributeId: sizeAttr?.attributeId || found.sizeAttributeId || '',
        sizeValueId: parsed.sizeValueId,
        sizeIsCustom: parsed.sizeIsCustom,
        sizeExtraValue: parsed.sizeExtraValue,
        sizeLabel: parsed.sizeLabel
          || found.sizeLabel
          || sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name
          || '',
      };
    }
    const platformName = !parsed.sizeIsCustom
      ? (sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name || '')
      : parsed.sizeLabel;
    return createSkuRow({
      sizeAttributeId: sizeAttr?.attributeId || '',
      sizeValueId: parsed.sizeValueId,
      sizeIsCustom: parsed.sizeIsCustom,
      sizeExtraValue: parsed.sizeExtraValue,
      sizeLabel: platformName,
      price: defaults.price || '',
      stock: defaults.stock || '',
      weight: defaults.weight || '',
      netWeight: defaults.netWeight || '',
      currency: defaults.currency || '',
    });
  });
}

function createEmptyAccessoryRow() {
  return { vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' };
}

function createSkuRow(partial = {}) {
  return {
    id: `sku-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeAttributeId: '',
    sizeValueId: '',
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
    supplierSku: '',
    price: '',
    specialPrice: '',
    currency: '',
    stock: '',
    weight: '',
    netWeight: '',
    length: '',
    width: '',
    height: '',
    quantity: '1',
    quantityUnit: '1',
    numberOfPieces: '1',
    pieceUnitCode: '1',
    totalContains: '',
    skuClassification: '1',
    individuallyPacked: '',
    packageType: '',
    isSensitive: 0,
    sensitiveList: [],
    suggestedPrice: '',
    suggestedPriceCurrencyType: '',
    accessories: [createEmptyAccessoryRow()],
    mallState: 1,
    skuImage: null,
    skuCode: '',
    siteRrpInfoList: [],
    ...partial,
  };
}

function createSkc(partial = {}) {
  return {
    id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    colorAttributeId: '',
    colorValueId: '',
    colorLabel: '',
    colorCode: '',
    colorIsCustom: false,
    colorExtraValue: '',
    supplierCode: '',
    skcName: '',
    mainImage: null,
    showroomSlots: {},
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    /** 颜色变体图（ATTRIBUTE_IMAGE / sku_img），在供应信息表维护 */
    skuImage: null,
    skuRows: [createSkuRow()],
    ...partial,
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}


function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        // 有比例（含 0）或显式 percent 字段 → 成分行；否则先保留 valueId，模板加载后再按 mode 转换
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

function emptyForm(initial = {}) {
  const rawTitleEn = String(initial.titleEn || initial.englishTitle || '').trim();
  const originRegionRaw = String(initial.originRegion || initial.originCountry || '').trim();
  let originRegion = 'CN';
  if (originRegionRaw) {
    if (/^china$/i.test(originRegionRaw) || originRegionRaw === '中国') {
      originRegion = 'CN';
    } else {
      originRegion = originRegionRaw;
    }
  }
  return {
    spuName: initial.spuName || '',
    title: initial.title || '',
    titleEn: rawTitleEn || '',
    titleLanguage: initial.titleLanguage || 'en',
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: String(initial.supplierCode || '').trim()
      || (Array.isArray(initial.skcList)
        ? (initial.skcList.map((skc) => String(skc?.supplierCode || '').trim()).find(Boolean) || '')
        : ''),
    brandCode: initial.brandCode || '',
    originCountry: originRegion,
    originRegion,
    originRegion2Id: initial.originRegion2Id != null ? String(initial.originRegion2Id) : '',
    originCertFiles: Array.isArray(initial.originCertFiles) ? initial.originCertFiles : [],
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    ...(() => {
      const imgs = resolveTiktokProductLevelImages(initial);
      return { mainImage: imgs.mainImage, detailImages: imgs.detailImages };
    })(),
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromTiktokPrintImages(initial.printImages),
    materialImage: initial.materialImage || null,
    materialImgUrl: initial.materialImgUrl || imageUrlOfSlot(initial.materialImage) || '',
    tiktokVideos: Array.isArray(initial.tiktokVideos) ? initial.tiktokVideos : [],
    guideFiles: Array.isArray(initial.guideFiles) ? initial.guideFiles : [],
    ...(() => {
      const coerced = coerceOuterPackageFormFields(initial);
      return {
        packageShape: coerced.packageShape,
        packageTypeOuter: coerced.packageTypeOuter,
        outerPackageImages: Array.isArray(initial.outerPackageImages)
          ? initial.outerPackageImages
          : [],
      };
    })(),
    freightTemplateId: initial.freightTemplateId || '',
    shipmentLimitSecond: initial.shipmentLimitSecond != null
      ? String(initial.shipmentLimitSecond)
      : '172800',
    isSensitive: initial.isSensitive != null ? Number(initial.isSensitive) : 0,
    goodsModels: Array.isArray(initial.goodsModels) ? initial.goodsModels : [],
    officialSizeChart: (() => {
      const draft = initial.officialSizeChart && typeof initial.officialSizeChart === 'object'
        ? { ...initial.officialSizeChart }
        : null;
      if (!draft) {
        // 仅有顶层 sizeChartData 时也组成可回填草稿
        if (initial.sizeChartData && typeof initial.sizeChartData === 'object') {
          return { mode: 'create', sizeChartData: initial.sizeChartData, records: [] };
        }
        return null;
      }
      if (!draft.sizeChartData && initial.sizeChartData && typeof initial.sizeChartData === 'object') {
        draft.sizeChartData = initial.sizeChartData;
      }
      return draft;
    })(),
    sizeChartImage: initial.sizeChartImage || null,
    sizeChartData: initial.sizeChartData && typeof initial.sizeChartData === 'object'
      ? initial.sizeChartData
      : (initial.officialSizeChart?.sizeChartData && typeof initial.officialSizeChart.sizeChartData === 'object'
        ? initial.officialSizeChart.sizeChartData
        : null),
    tiktokSizeTemplateIds: Array.isArray(initial.tiktokSizeTemplateIds)
      ? initial.tiktokSizeTemplateIds.map(String)
      : [],
    sizeChart: initial.sizeChart || {},
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    // 商品级包裹：优先表单字段；旧草稿只写在 SKU 行时回填
    weightGrams: initial.weightGrams || firstFilledSkuPackageField(initial.skcList, 'weight') || '',
    netWeightGrams: initial.netWeightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || firstFilledSkuPackageField(initial.skcList, 'length') || '10',
    pkgWidth: initial.pkgWidth || firstFilledSkuPackageField(initial.skcList, 'width') || '10',
    pkgHeight: initial.pkgHeight || firstFilledSkuPackageField(initial.skcList, 'height') || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    manufacturerIds: Array.isArray(initial.manufacturerIds) ? initial.manufacturerIds.map(String) : [],
    responsiblePersonIds: Array.isArray(initial.responsiblePersonIds)
      ? initial.responsiblePersonIds.map(String)
      : [],
    certifications: Array.isArray(initial.certifications) ? initial.certifications : [],
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'tiktok',
    /**
     * 上架方式：'1' = 自动上架（立即），'2' = 预约上架。
     * 与 scheduled_sale 的唯一开关 hopeOnSaleDate 保持一致：旧数据只填了时间没写方式时补 '2'。
     */
    shelfWay: initial.shelfWay === '2' || (!initial.shelfWay && initial.hopeOnSaleDate) ? '2' : '1',
    /** 预约上架（scheduled_sale）：datetime-local，空 = 立即上架 */
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
    /** 搜索词（search_terms）：多行文本，一行一个，≤15 条 / 合计 ≤250 字符 */
    searchTerms: tiktokMultilineText(initial.searchTerms),
    /** 商品卖点（key_product_features）：多行文本，一行一条，≤5 条 / 合计 ≤1500 字符 */
    keyProductFeatures: tiktokMultilineText(initial.keyProductFeatures),
    /** 限购（purchase_order_quantity_limit）：空 = 不限 */
    maxOrderQuantity: initial.maxOrderQuantity != null ? String(initial.maxOrderQuantity) : '',
    cumulativeOrderQuantity: initial.cumulativeOrderQuantity != null
      ? String(initial.cumulativeOrderQuantity)
      : '',
    /** 预售（skus[].pre_sale）：'' = 非预售；仅类目规则允许时展示 */
    preSaleType: initial.preSaleType || '',
    preSaleHandlingDays: initial.preSaleHandlingDays != null ? String(initial.preSaleHandlingDays) : '',
    preSaleReleaseDate: initial.preSaleReleaseDate || '',
  };
}

export default function ProductTemplateEditor({
  templateId: initialTemplateId = '',
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  referenceShopId = '',
  afterSaveNavigate,
}) {
  const { t, i18n } = useTranslation('workbench');
  const navigate = useNavigate();
  const tiktokShopCtx = useTiktokShopOptional();
  const activeShop = tiktokShopCtx?.activeShop ?? null;
  const shopId = referenceShopId || activeShop?.id;
  /** 官方 catalog 借店：制造商/责任人 + 尺码表类目规则（不拉店铺仓/运费） */
  const [catalogReferenceShopId, setCatalogReferenceShopId] = useState('');
  const complianceShopId = shopId || catalogReferenceShopId || '';
  /** 尺码表规则/填写：店铺模板用本店；官方模板用 URL 参考店或 catalog 借店 */
  const sizeChartShopId = complianceShopId;
  const shopTemplateQuery = officialMode ? {} : { scope: templateScope };
  const shopCaps = useMemo(
    () => tiktokShopCapabilities(officialMode ? shopType : (activeShop?.shopType || shopType)),
    [officialMode, shopType, activeShop?.shopType],
  );
  const isSemiManaged = shopCaps.mode === TIKTOK_MODE_SEMI;
  const skipSiteWarehouseFreight = Boolean(shopCaps.skipSiteWarehouseFreight);
  const priceMode = shopCaps.canUpdateSalePrice ? 'sale' : (shopCaps.canUpdateCost ? 'cost' : '');
  const ttsPublishMode = isTiktokTtsPublishMode();
  const hideTitleEn = hideTiktokPublishField('titleEn');
  const hideProductOrigin = hideTiktokPublishField('productOrigin');
  const hideGuideFiles = hideTiktokPublishField('guideFiles');
  const hideTemuSkuPackageExtras = hideTiktokPublishField('temuSkuPackageExtras');
  const hideOuterPackage = hideTiktokPublishField('outerPackage');
  const hideGoodsModels = hideTiktokPublishField('goodsModels');
  const hideListingSites = hideTiktokPublishField('listingSites');
  const hideShipmentLimit = hideTiktokPublishField('shipmentLimit');
  const [templateId, setTemplateId] = useState(() => (
    initialTemplateId ? String(initialTemplateId) : ''
  ));
  const [templateMeta, setTemplateMeta] = useState({
    name: '',
    remark: '',
    autoRuleEnabled: false,
    sortOrder: 0,
    styleNo: '',
  });
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(() => Boolean(initialTemplateId));
  const [templateHydrated, setTemplateHydrated] = useState(() => Boolean(!initialTemplateId));
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [form, setForm] = useState(() => emptyForm({}));
  const [meta, setMeta] = useState({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
  const [metaLoading, setMetaLoading] = useState(false);
  /** 半托管/全托管：发布规范 【currency】；自运营：query-site-list 自营站点币种 */
  const [publishCurrencies, setPublishCurrencies] = useState([]);
  const [publishCurrencyLoading, setPublishCurrencyLoading] = useState(false);
  /** 按末级类目加载的发布规范：门控 SKU 件数 / 包装类型 */
  const [skuPkgCaps, setSkuPkgCaps] = useState(EMPTY_SKU_PACKAGE_CAPS);
  const [skuPkgCapsLoading, setSkuPkgCapsLoading] = useState(false);
  /** Tiktok 不走 Shein 关联属性填写规则 */
  const [attrTpl, setAttrTpl] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [attrError, setAttrError] = useState('');
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [attrTemplates, setAttrTemplates] = useState([]);
  const [selectedAttrTemplateId, setSelectedAttrTemplateId] = useState('');
  const [selectedSizeChartTemplateId, setSelectedSizeChartTemplateId] = useState('');
  const [selectedAttrTemplateVersion, setSelectedAttrTemplateVersion] = useState(null);
  const [selectedSizeChartTemplateVersion, setSelectedSizeChartTemplateVersion] = useState(null);
  const [refVersionConflicts, setRefVersionConflicts] = useState({});
  const [refChangePreviews, setRefChangePreviews] = useState({});
  const [reloadingRefType, setReloadingRefType] = useState('');
  const [attrTemplateBusy, setAttrTemplateBusy] = useState(false);
  const [attrSaveModalOpen, setAttrSaveModalOpen] = useState(false);
  /** 分类下可自定义属性值的 attributeId 集合 */
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  /** 类目切换后待自动引用的 productTypeId（空=不自动） */
  const pendingAttrAutoForRef = useRef('');
  /** 切类目时暂存旧类目已填参数，待新 schema 就绪后按相似字段回填 */
  const pendingCategoryPreserveRef = useRef(null);
  const attrTemplatesLoadedForRef = useRef('');
  /** 上次属性库自动套用所用的款号（小写）；货号变更时重匹配 */
  const lastAttrAutoStyleKeyRef = useRef(null);
  const attrTplRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pkgBatchMode, setPkgBatchMode] = useState(null);
  const [freightOptions, setFreightOptions] = useState([]);
  const [freightLoading, setFreightLoading] = useState(false);
  /** 品牌下拉：来自「品牌库」本地缓存（Get Brands / 自定义品牌），与类目是否已选无关 */
  const [brandSelectOptions, setBrandSelectOptions] = useState([]);
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);
  const [brandKeyword, setBrandKeyword] = useState('');
  /** 品牌库为空：给出「去品牌库同步」提示，避免只看到空下拉 */
  const [brandLibraryEmpty, setBrandLibraryEmpty] = useState(false);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [accessoryLoading, setAccessoryLoading] = useState(false);
  const [accessoryLoadError, setAccessoryLoadError] = useState('');
  const [accessoryReloadKey, setAccessoryReloadKey] = useState(0);
  /** 尺码表：与发品页同款官方尺码表面板 */
  const [sizeChartRequired, setSizeChartRequired] = useState(false);
  const [sizeChartClass, setSizeChartClass] = useState(null);
  const [sizeChartClassLoading, setSizeChartClassLoading] = useState(false);
  const [sizeChartClassError, setSizeChartClassError] = useState('');
  const officialSizeChartRef = useRef(null);
  /** 防连点：state 的 disabled 来不及挡住同帧第二次 click */
  const savingTemplateLockRef = useRef(false);
  /** Get Category Rules：认证 / COD / PFAND / 制造商 / 欧盟负责人 */
  const [categoryRules, setCategoryRules] = useState(EMPTY_TIKTOK_CATEGORY_RULES);
  const [categoryRulesLoading, setCategoryRulesLoading] = useState(false);
  const [categoryRulesError, setCategoryRulesError] = useState('');
  /** 店铺市场二字码：驱动欧盟制造商/责任人是否展示 */
  const [marketRegion, setMarketRegion] = useState('');
  const [aiTitleBusy, setAiTitleBusy] = useState(false);
  const [aiDescBusy, setAiDescBusy] = useState(false);
  const [, setTitleTranslateBusy] = useState(false);
  const titleTranslateSeqRef = useRef(0);
  const lastAutoTitleEnRef = useRef('');

  const categoryOptions = useMemo(() => flattenLeafCategories(meta.categories), [meta.categories]);
  const siteOptions = useMemo(() => listEnabledSites(meta.sites), [meta.sites]);
  const siteSelectOptions = useMemo(() => {
    void i18n.language;
    if (isSemiManaged) {
      return labeledSiteOptions('tiktokSite', TIKTOK_SEMI_SITES);
    }
    return siteOptions.map((site) => ({
      value: site.siteAbbr,
      label: site.siteName || site.siteAbbr,
    }));
  }, [isSemiManaged, siteOptions, i18n.language]);

  /** 半托管/全托管走发布规范币种；自运营走站点列表（随已选站点对齐） */
  const platformCurrency = useMemo(() => {
    if (shopCaps.usesPriceInfo) {
      return pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites: form.selectedSites,
      });
    }
    return pickPublishCurrency(publishCurrencies, form.currency);
  }, [form.currency, form.selectedSites, meta.sites, publishCurrencies, shopCaps.usesPriceInfo]);

  const applyPublishCurrency = (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next) return;
    setForm((prev) => {
      if (String(prev.currency || '').trim().toUpperCase() === next) return prev;
      return {
        ...prev,
        currency: next,
        skcList: (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            currency: next,
          })),
        })),
      };
    });
  };

  const selectedSitesKey = useMemo(
    () => (Array.isArray(form.selectedSites) ? form.selectedSites.map(String).filter(Boolean).join('|') : ''),
    [form.selectedSites],
  );

  // 币种就绪后回写表单：半托管/全托管按发布规范；自运营按站点列表（已选站点优先）。
  useEffect(() => {
    if (!publishCurrencies.length) return;
    const selectedSites = selectedSitesKey ? selectedSitesKey.split('|') : [];
    const next = shopCaps.usesPriceInfo
      ? pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites,
      })
      : pickPublishCurrency(publishCurrencies, form.currency);
    if (!next) return;
    if (String(form.currency || '').trim().toUpperCase() === String(next).trim().toUpperCase()) return;
    applyPublishCurrency(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 规范/站点币种或回填变化时对齐
  }, [publishCurrencies, form.currency, selectedSitesKey, meta.sites, shopCaps.usesPriceInfo]);

  const warehouseOptions = useMemo(() => {
    const rows = filterTiktokSalesWarehouses(meta.warehouses);
    const mapped = rows
      .map((item) => {
        const id = String(item.id || item.warehouseCode || '');
        const name = String(item.name || item.warehouseName || '').trim();
        return {
          value: id,
          // 下拉只展示仓库名称；提交仍用 value=warehouseCode
          label: name || id,
          name: name || id,
        };
      })
      .filter((item) => item.value);
    if (form.warehouseId && !mapped.some((item) => item.value === String(form.warehouseId))) {
      const stillSales = filterTiktokSalesWarehouses(meta.warehouses)
        .some((item) => String(item.id || item.warehouseCode || '') === String(form.warehouseId));
      if (stillSales) {
        const fallbackName = String(form.warehouseName || '').trim();
        mapped.unshift({
          value: String(form.warehouseId),
          label: fallbackName || String(form.warehouseId),
          name: fallbackName || String(form.warehouseId),
        });
      }
    }
    return mapped;
  }, [meta.warehouses, form.warehouseId, form.warehouseName]);
  const multiWarehouse = warehouseOptions.length > 1;

  // 库存仓库：店铺模板默认第一个发货仓（排除退货仓）。
  // 官方模板发货仓选填且不自动带入；全托管/本土跳过站点·仓·运费。
  useEffect(() => {
    if (skipSiteWarehouseFreight || officialMode) return;
    const rows = filterTiktokSalesWarehouses(meta.warehouses);
    if (!rows.length) return;
    setForm((prev) => {
      const current = String(prev.warehouseId || '').trim();
      const codes = rows
        .map((item) => String(item.id || item.warehouseCode || '').trim())
        .filter(Boolean);
      if (current && codes.includes(current)) {
        // 补全名称（仅有 code 时）
        if (!String(prev.warehouseName || '').trim()) {
          const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
          const name = hit?.name || hit?.warehouseName || '';
          if (name) return { ...prev, warehouseName: name };
        }
        return prev;
      }
      const first = rows[0];
      const id = String(first.id || first.warehouseCode || '').trim();
      if (!id) return prev;
      return {
        ...prev,
        warehouseId: id,
        warehouseName: first.name || first.warehouseName || '',
      };
    });
  }, [meta.warehouses, form.warehouseId, skipSiteWarehouseFreight, officialMode]);

  // 品牌下拉：始终从「品牌库」本地缓存（/shops/{id}/brands）加载，不依赖类目是否已选；
  // 输入关键字时按品牌名/品牌 ID 后端模糊查询（品牌库可能有上百条，避免一次性全量拉取）。
  // 官方模板不带品牌（店铺套用发品时再选本店品牌），故 officialMode 不预加载。
  useEffect(() => {
    if (!shopId || officialMode) {
      setBrandSelectOptions([]);
      setBrandLibraryEmpty(false);
      setBrandOptionsLoading(false);
      return undefined;
    }
    const keyword = brandKeyword.trim();
    let cancelled = false;
    const timer = setTimeout(() => {
      setBrandOptionsLoading(true);
      tiktokPlatformAPI.getShopBrands(shopId, {
        pageSize: BRAND_DROPDOWN_PAGE_SIZE,
        brandName: keyword || undefined,
      })
        .then((res) => {
          if (cancelled) return;
          const rows = res?.brands ?? res;
          setBrandSelectOptions(shopBrandsToSelectOptions(rows));
          const total = Number(res?.total ?? res?.total_count);
          setBrandLibraryEmpty(
            !keyword && (Number.isFinite(total) ? total <= 0 : !Array.isArray(rows) || rows.length === 0),
          );
        })
        .catch(() => {
          if (cancelled) return;
          setBrandSelectOptions([]);
          setBrandLibraryEmpty(false);
        })
        .finally(() => {
          if (!cancelled) setBrandOptionsLoading(false);
        });
    }, keyword ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shopId, officialMode, brandKeyword]);

  // 模板里已存的品牌可能不在首页结果里：补一条占位选项，避免下拉显示为空
  const brandFieldOptions = useMemo(() => {
    const value = String(form.brandCode || '').trim();
    if (!value || brandSelectOptions.some((opt) => String(opt.value) === value)) {
      return brandSelectOptions;
    }
    return [{ value, label: value }, ...brandSelectOptions];
  }, [brandSelectOptions, form.brandCode]);

  // 运费模板（Create Product shipping_template_id 选填；官方/全托/本土不预加载店铺运费）
  useEffect(() => {
    if (!shopId || officialMode || skipSiteWarehouseFreight) {
      setFreightOptions([]);
      setFreightLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFreightLoading(true);
    tiktokPlatformAPI.listShopFreightTemplates(shopId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.items)
          ? res.items
          : (Array.isArray(res?.freightTemplates) ? res.freightTemplates : []);
        setFreightOptions(rows.map((row) => {
          const id = String(row.shippingTemplateId || row.templateId || row.freightTemplateId || row.id || '').trim();
          const name = String(row.templateName || row.name || id).trim();
          const suffix = row.isAvailable === false
            ? i18n.t('templateEditor.unavailable', { ns: 'workbench', defaultValue: fixedUi('（不可用）') })
            : (row.isDefault === true
              ? i18n.t('templateEditor.defaultMark', { ns: 'workbench', defaultValue: fixedUi('（默认）') })
              : '');
          return { value: id, label: `${name || id}${suffix}` };
        }).filter((row) => row.value));
        setForm((prev) => {
          const cur = String(prev.freightTemplateId || '').trim();
          if (!cur) return prev;
          const hit = rows.find((t) => {
            const id = String(t.shippingTemplateId || t.templateId || t.freightTemplateId || t.id || '').trim();
            return id === cur;
          });
          if (hit && hit.isAvailable === false) {
            return { ...prev, freightTemplateId: '' };
          }
          return prev;
        });
      })
      .catch(() => {
        if (!cancelled) setFreightOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFreightLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, skipSiteWarehouseFreight, i18n]);

  // 包装清单选项（TTS 发品隐藏 Temu 独有字段，跳过拉取）
  useEffect(() => {
    if (hideTemuSkuPackageExtras || !shopId || officialMode || !form.categoryId) {
      setAccessoryOptions([]);
      setAccessoryLoadError('');
      setAccessoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAccessoryLoading(true);
    setAccessoryLoadError('');
    tiktokPlatformAPI.listPublishAccessories(shopId, { page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res?.result?.data) ? res.result.data : []);
        const opts = (rows || []).map((row) => ({
          value: String(row.vid),
          label: String(row.value || row.vid),
          unitCode: Number(row.unitCode || 1),
          unitName: String(row.unitName || '件'),
          thumbUrl: String(row.thumbUrl || ''),
        })).filter((o) => o.value);
        setAccessoryOptions(opts);
        if (!opts.length) {
          setAccessoryLoadError(t('publishPkg.accEmpty'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAccessoryOptions([]);
        const msg = String(e?.message || e?.userMessage || t('publishPkg.accLoadFail')).trim();
        setAccessoryLoadError(msg);
        showToast(t('publishPkg.accLoadFailWrap', { message: msg }), 'warning');
      })
      .finally(() => {
        if (!cancelled) setAccessoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, form.categoryId, accessoryReloadKey, hideTemuSkuPackageExtras, t]);

  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrsBase = attrTpl?.sizeAttrs;
  const sizeMeasureAttrs = useMemo(
    () => (Array.isArray(sizeMeasureAttrsBase) ? sizeMeasureAttrsBase : []),
    [sizeMeasureAttrsBase],
  );
  const selectedSizeLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    const push = (key, label) => {
      const k = String(key || '').trim();
      const name = String(label || '').trim();
      if (!k || seen.has(k) || !name) return;
      seen.add(k);
      labels.push(name);
    };
    (form.skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        const key = skuSizeKey(sku);
        const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId));
        push(
          key,
          sku.sizeLabel || sku.sizeExtraValue || fromOpt?.name || attributeValueDisplayLabel(fromOpt),
        );
      });
    });
    (form.selectedSizeIds || []).forEach((id) => {
      const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(id));
      push(id, fromOpt?.name || attributeValueDisplayLabel(fromOpt) || id);
    });
    return labels;
  }, [form.skcList, form.selectedSizeIds, sizeAttr]);
  const modelTryOnSizeOptions = useMemo(() => buildTiktokModelTryOnSizeOptions({
    sizeAttr,
    attrTpl,
    selectedSizeIds: form.selectedSizeIds,
    skcList: form.skcList,
  }), [sizeAttr, attrTpl, form.selectedSizeIds, form.skcList]);
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const completion = useMemo(
    () => calcPublishCompletion(form, attrTpl, { hideTitleEn }),
    [form, attrTpl, hideTitleEn],
  );

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  /**
   * 商品级包裹重量/尺寸：同步到全部 SKU 行，避免旧草稿路径仍读 SKU 字段时不一致。
   * @param {{ weightGrams?: string, pkgLength?: string, pkgWidth?: string, pkgHeight?: string }} patch
   */
  const updateProductPackage = (patch) => {
    const nextPatch = patch && typeof patch === 'object' ? patch : {};
    setForm((prev) => {
      const skuPatch = {};
      if ('weightGrams' in nextPatch) skuPatch.weight = nextPatch.weightGrams;
      if ('pkgLength' in nextPatch) skuPatch.length = nextPatch.pkgLength;
      if ('pkgWidth' in nextPatch) skuPatch.width = nextPatch.pkgWidth;
      if ('pkgHeight' in nextPatch) skuPatch.height = nextPatch.pkgHeight;
      const skcList = Object.keys(skuPatch).length
        ? (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...skuPatch })),
        }))
        : prev.skcList;
      return { ...prev, ...nextPatch, skcList };
    });
    setFieldErrors((prev) => {
      if (!prev) return prev;
      const keys = ['weightGrams', 'pkgLength', 'pkgWidth', 'pkgHeight', 'form'];
      if (!keys.some((k) => prev[k])) return prev;
      const next = { ...prev };
      keys.forEach((k) => { delete next[k]; });
      return next;
    });
  };

  /**
   * 预约上架：与 shelfWay 同步（'2' = 预约上架，'1' = 自动/立即上架）。
   * 记录列表与导出按 shelfWay 判断展示「自动上架」还是预约时间，故必须同写。
   */
  const updateScheduledSaleAt = (value) => {
    const next = String(value || '');
    setForm((prev) => ({ ...prev, hopeOnSaleDate: next, shelfWay: next ? '2' : '1' }));
    setFieldErrors((prev) => {
      if (!prev?.hopeOnSaleDate && !prev?.form) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.hopeOnSaleDate;
      delete nextErrors.form;
      return nextErrors;
    });
  };

  /**
   * 上架方式切换（同 SHEIN）：'1' = 自动上架（套用该模板发品即上架），'2' = 预约上架。
   * 切回自动上架必须清掉已选时间，否则仍会带上 scheduled_sale。
   */
  const updateShelfWay = (value) => {
    const next = String(value || '') === '2' ? '2' : '1';
    setForm((prev) => ({
      ...prev,
      shelfWay: next,
      hopeOnSaleDate: next === '2' ? prev.hopeOnSaleDate : '',
    }));
    setFieldErrors((prev) => {
      if (next === '2' || (!prev?.hopeOnSaleDate && !prev?.form)) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.hopeOnSaleDate;
      delete nextErrors.form;
      return nextErrors;
    });
  };

  const setAttrValue = (attributeId, value) => {
    setForm((prev) => {
      const draft = { ...prev.productAttributes, [attributeId]: value };
      const pool = [
        ...(attrTpl?.productAttrs || []),
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.optionalProduct || []),
      ];
      const seen = new Set();
      const allAttrs = pool.filter((a) => {
        const id = String(a?.attributeId || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const pruned = pruneInvisibleProductAttributes(draft, allAttrs);
      return { ...prev, productAttributes: pruned };
    });
  };

  /** 自定义属性值写入本地属性模板，供下拉即时可选 */
  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    setAttrTpl((prev) => {
      if (!prev) return prev;
      const patchList = (list) => (list || []).map((attr) => {
        if (String(attr.attributeId) !== String(attributeId)) return attr;
        if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
        return {
          ...attr,
          values: [...(attr.values || []), {
            id: String(opt.id),
            name: opt.name,
            nameCn: opt.nameCn || opt.name,
          }],
        };
      });
      return {
        ...prev,
        productAttrs: patchList(prev.productAttrs),
        requiredProduct: patchList(prev.requiredProduct),
        mainAttrs: patchList(prev.mainAttrs),
        otherAttrs: patchList(prev.otherAttrs),
        optionalProduct: patchList(prev.optionalProduct),
        skcSaleAttrs: patchList(prev.skcSaleAttrs),
        skuSaleAttrs: patchList(prev.skuSaleAttrs),
      };
    });
  };

  const updateSkc = (skcId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => (skc.id === skcId ? { ...skc, ...patch } : skc)),
    }));
  };

  const updatePrintImages = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    // 印花图仅站内展示 / 侵权检测用，不自动回填货号与商家 SKU，也不提交平台
    setForm((prev) => ({
      ...prev,
      printImages: list,
      ipePrintImageNames: printNamesFromTiktokPrintImages(list),
    }));
  };

  const onMaterialImagesChange = (next) => {
    // PublishImageUploadZone 单图模式传对象/null；多图模式传数组
    const img = Array.isArray(next) ? (next[0] || null) : (next || null);
    setForm((prev) => ({
      ...prev,
      materialImage: img,
      materialImgUrl: String(img?.tiktokUrl || '').trim(),
    }));
  };

  const onVideosChange = (videos) => {
    setForm((prev) => ({
      ...prev,
      tiktokVideos: Array.isArray(videos) ? videos : [],
    }));
    setFieldErrors((prev) => {
      if (!prev?.tiktokVideos) return prev;
      const next = { ...prev };
      delete next.tiktokVideos;
      return next;
    });
  };

  const updateSku = (skcId, skuId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        return {
          ...skc,
          skuRows: skc.skuRows.map((sku) => (sku.id === skuId ? { ...sku, ...patch } : sku)),
        };
      }),
    }));
  };

  const batchUpdateSku = (patchOrMapper = {}) => {
    if (typeof patchOrMapper === 'function') {
      setForm((prev) => ({
        ...prev,
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => {
            const patch = patchOrMapper(skc, sku);
            if (!patch || typeof patch !== 'object') return sku;
            return { ...sku, ...patch };
          }),
        })),
      }));
      showToast(t('templateEditor.skuBatchUpdated'), 'success');
      return;
    }
    const next = {};
    [
      'price',
      'suggestedPrice',
      'suggestedPriceCurrencyType',
      'stock',
      'supplierSku',
      'identifierCode',
      'identifierType',
      'weight',
      'netWeight',
      'length',
      'width',
      'height',
      'quantity',
      'quantityUnit',
      'packageType',
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(patchOrMapper, key)) next[key] = patchOrMapper[key];
    });
    if (!Object.keys(next).length) return;
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => ({
        ...skc,
        skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...next })),
      })),
    }));
    showToast(t('publishPkg.appliedAll'), 'success');
  };

  const removeSku = (skcId, skuId) => {
    setForm((prev) => {
      const nextSkcList = prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        const nextRows = skc.skuRows.filter((sku) => sku.id !== skuId);
        return {
          ...skc,
          skuRows: nextRows.length
            ? nextRows
            : [createSkuRow({ sizeAttributeId: sizeAttr?.attributeId || '' })],
        };
      });
      return {
        ...prev,
        skcList: nextSkcList,
        selectedSizeIds: deriveSelectedSizeIds(nextSkcList),
      };
    });
  };

  const skuDefaultsFromForm = (prev) => ({
    price: prev.supplyPrice || '',
    stock: prev.stockQty || '',
    weight: prev.weightGrams || '',
    netWeight: prev.netWeightGrams || '',
    currency: prev.currency || '',
  });

  const onColorsChange = (colorKeys) => {
    const platformIds = new Set((colorAttr?.values || []).map((v) => String(v.id)));
    const safeKeys = [...new Set((colorKeys || []).map(String).filter((key) => {
      if (!key) return false;
      if (key.startsWith('custom:')) return Boolean(key.slice('custom:'.length));
      return platformIds.has(key);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      const sizeIds = (prev.selectedSizeIds || [])
        .map(String)
        .filter((id) => (sizeAttr?.values || []).some((v) => String(v.id) === id));
      const nextSkcList = safeKeys.map((colorKey) => {
        const isCustom = colorKey.startsWith('custom:');
        let colorExtraValue = '';
        if (isCustom) {
          try {
            colorExtraValue = decodeURIComponent(colorKey.slice('custom:'.length));
          } catch {
            colorExtraValue = colorKey.slice('custom:'.length);
          }
        }
        const existing = prev.skcList.find((skc) => {
          if (isCustom) {
            return Boolean(skc.colorIsCustom || skc.colorExtraValue)
              && String(skc.colorExtraValue || skc.colorLabel || '') === colorExtraValue;
          }
          return String(skc.colorValueId || '') === colorKey && !skc.colorIsCustom;
        });
        const val = !isCustom
          ? colorAttr?.values?.find((v) => String(v.id) === colorKey)
          : null;
        const colorLabel = isCustom
          ? colorExtraValue
          : (val?.nameCn || val?.name || existing?.colorLabel || '');
        if (existing) {
          return {
            ...existing,
            colorAttributeId: colorAttr?.attributeId || existing.colorAttributeId,
            colorValueId: isCustom ? '' : colorKey,
            colorLabel,
            colorCode: existing.colorCode || '',
            colorIsCustom: isCustom,
            colorExtraValue: isCustom ? colorExtraValue : '',
            skuRows: buildSkuRowsForSizes(existing.skuRows, sizeIds, sizeAttr, defaults),
          };
        }
        return createSkc({
          colorAttributeId: colorAttr?.attributeId || '',
          colorValueId: isCustom ? '' : colorKey,
          colorLabel,
          colorCode: '',
          colorIsCustom: isCustom,
          colorExtraValue: isCustom ? colorExtraValue : '',
          skuRows: buildSkuRowsForSizes([], sizeIds, sizeAttr, defaults),
        });
      });
      return { ...prev, skcList: nextSkcList, selectedSizeIds: sizeIds };
    });
  };

  const onSizesChange = (sizeIds) => {
    const allowed = new Set((sizeAttr?.values || []).map((v) => String(v.id)));
    const safeIds = [...new Set((sizeIds || []).map(String).filter((id) => {
      if (isCustomSpecKey(id)) return Boolean(customLabelFromKey(id));
      return allowed.has(id);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      return {
        ...prev,
        selectedSizeIds: safeIds,
        sizeSaleAttributeId: sizeAttr?.attributeId || prev.sizeSaleAttributeId || '',
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: buildSkuRowsForSizes(skc.skuRows, safeIds, sizeAttr, defaults),
        })),
      };
    });
  };

  const updateSizeCell = (sizeValueId, attrId, value) => {
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: {
        ...prev.sizeChart,
        [sizeValueId]: {
          ...(prev.sizeChart?.[sizeValueId] || {}),
          [attrId]: value,
        },
      },
    }));
  };

  const applySizeChart = (nextChart, options = {}) => {
    const replace = options?.replace === true;
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: replace
        ? { ...(nextChart || {}) }
        : {
          ...(prev.sizeChart || {}),
          ...(nextChart || {}),
        },
    }));
  };

  useEffect(() => {
    if (!initialTemplateId) {
      setTemplateLoading(false);
      setTemplateHydrated(true);
      return undefined;
    }
    if (!officialMode && !shopId) {
      return undefined;
    }
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateHydrated(false);
    const loadPromise = officialMode
      ? adminAPI.getOfficialTiktokProductTemplate(initialTemplateId).then((res) => res?.item || res?.data?.item || res)
      : tiktokPlatformAPI.getProductTemplate(shopId, initialTemplateId, {
        scope: isOfficialProductTemplateScope(templateScope) ? 'OFFICIAL' : templateScope,
      }).then((res) => res?.item || res);
    loadPromise
      .then((item) => {
        if (cancelled || !item) return;
        const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        const formData = rawPayload.form && typeof rawPayload.form === 'object'
          ? rawPayload.form
          : rawPayload;
        setTemplateId(String(item.id || initialTemplateId));
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        setTemplateMeta({
          name: String(item.name || '').trim(),
          remark: String(item.remark || '').trim(),
          autoRuleEnabled: item.autoRuleEnabled === true || item.autoRuleEnabled === 1,
          sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0,
          styleNo: String(item.styleNo || '').trim(),
        });
        setForm(sanitizeTiktokFormImageSlots(emptyForm({
          ...formData,
          // 表字段为列表/批量更换类目的权威来源，优先于 payload.form 内可能过期的类目
          categoryId: item.categoryId || formData.categoryId || '',
          productTypeId: item.productTypeId || formData.productTypeId || '',
          categoryPathLabel: item.categoryPathLabel || formData.categoryPathLabel || '',
        })));
        setSelectedAttrTemplateId(String(
          rawPayload.selectedAttrTemplateId
          || formData.selectedAttrTemplateId
          || '',
        ).trim());
        setSelectedSizeChartTemplateId(String(
          rawPayload.selectedSizeChartTemplateId
          || formData.selectedSizeChartTemplateId
          || '',
        ).trim());
        setSelectedAttrTemplateVersion(parseTemplateVersion(
          rawPayload.selectedAttrTemplateVersion
          ?? formData.selectedAttrTemplateVersion,
        ));
        setSelectedSizeChartTemplateVersion(parseTemplateVersion(
          rawPayload.selectedSizeChartTemplateVersion
          ?? formData.selectedSizeChartTemplateVersion,
        ));
        setRefVersionConflicts({});
        setTemplateHydrated(true);
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || t('templateEditor.templateLoadFail'), 'error');
        setTemplateHydrated(true);
        navigate(
          afterSaveNavigate || buildTiktokPath('templates/product'),
          { replace: true },
        );
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => { cancelled = true; };
  }, [initialTemplateId, shopId, officialMode, templateScope, navigate, afterSaveNavigate, t]);

  // 校验已套用属性库仍属于当前范围；官方复制残留 ID 会 404，解开引用但保留已填写属性
  useEffect(() => {
    if ((!officialMode && !shopId) || !templateHydrated) return undefined;
    const attrId = String(selectedAttrTemplateId || '').trim();
    if (!attrId) return undefined;
    let cancelled = false;
    fetchAttrTemplateDetail(officialMode, shopId, attrId, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        setSelectedAttrTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || officialMode || !isMissingTemplateError(e)) return;
        setSelectedAttrTemplateId('');
        setSelectedAttrTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, templateHydrated, selectedAttrTemplateId, templateScope]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !templateHydrated) return undefined;
    const sizeId = String(selectedSizeChartTemplateId || '').trim();
    if (!sizeId) return undefined;
    let cancelled = false;
    fetchSizeChartTemplateDetail(officialMode, shopId, sizeId, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        setSelectedSizeChartTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || officialMode || !isMissingTemplateError(e)) return;
        setSelectedSizeChartTemplateId('');
        setSelectedSizeChartTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, templateHydrated, selectedSizeChartTemplateId, templateScope]);

  useEffect(() => {
    if (!sizeChartShopId || !form.categoryId) {
      setSizeChartRequired(false);
      setSizeChartClass(null);
      setSizeChartClassError(
        officialMode && form.categoryId && !sizeChartShopId
          ? t('templateEditor.noRefShop')
          : '',
      );
      setSizeChartClassLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSizeChartClassLoading(true);
    setSizeChartClassError('');
    fetchSizeChartClassRequired(sizeChartShopId, form.categoryId)
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setSizeChartRequired(false);
          setSizeChartClass(null);
          setSizeChartClassError(res?.error || res?.message || t('templateEditor.sizeClassFailShort'));
          showToast(res?.error || res?.message || t('templateEditor.sizeClassFail'), 'error');
          return;
        }
        setSizeChartRequired(Boolean(res.required));
        setSizeChartClass(res.sizeClass || null);
        setSizeChartClassError('');
        if (!res.required) {
          setForm((prev) => (
            (prev.tiktokSizeTemplateIds || []).length
              ? { ...prev, tiktokSizeTemplateIds: [] }
              : prev
          ));
        }
      })
      .finally(() => {
        if (!cancelled) setSizeChartClassLoading(false);
      });
    return () => { cancelled = true; };
  }, [sizeChartShopId, form.categoryId, officialMode, t]);

  const onOfficialSizeChartDraftChange = useCallback((draft) => {
    const nextDraft = draft && typeof draft === 'object' ? draft : null;
    setForm((prev) => {
      if (prev.officialSizeChart === nextDraft) return prev;
      if (
        officialSizeChartDraftSignature(prev.officialSizeChart)
        === officialSizeChartDraftSignature(nextDraft)
      ) {
        return prev;
      }
      // 面板 meta 未就绪时可能短暂回传空 records：勿覆盖已有尺寸，否则保存/再编辑会丢
      const prevRecords = prev.officialSizeChart?.records;
      const nextRecords = nextDraft?.records;
      const prevHas = Array.isArray(prevRecords) && prevRecords.some((row) => {
        const values = row?.values && typeof row.values === 'object' ? row.values : null;
        if (!values) return false;
        return Object.entries(values).some(([k, v]) => (
          k !== '1' && k !== 'sizeName' && k !== 'groupId' && k !== 'name'
          && v != null && String(v).trim() !== ''
        ));
      });
      const nextHas = Array.isArray(nextRecords) && nextRecords.some((row) => {
        const values = row?.values && typeof row.values === 'object' ? row.values : null;
        if (!values) return false;
        return Object.entries(values).some(([k, v]) => (
          k !== '1' && k !== 'sizeName' && k !== 'groupId' && k !== 'name'
          && v != null && String(v).trim() !== ''
        ));
      });
      if (prevHas && !nextHas) return prev;
      return {
        ...prev,
        officialSizeChart: nextDraft,
        ...(nextDraft?.sizeChartData ? { sizeChartData: nextDraft.sizeChartData } : {}),
      };
    });
  }, []);

  useEffect(() => {
    if ((!officialMode && !shopId) || !form.categoryId) {
      setCategoryRules(EMPTY_TIKTOK_CATEGORY_RULES);
      setCategoryRulesError('');
      setCategoryRulesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCategoryRulesLoading(true);
    setCategoryRulesError('');
    const load = officialMode
      ? Promise.resolve(null)
      : tiktokPlatformAPI.getPublishCategoryRules(shopId, form.categoryId);
    load
      .then((res) => {
        if (cancelled) return;
        setCategoryRules(res ? normalizeTiktokCategoryRules(res) : EMPTY_TIKTOK_CATEGORY_RULES);
      })
      .catch((e) => {
        if (cancelled) return;
        setCategoryRules(EMPTY_TIKTOK_CATEGORY_RULES);
        setCategoryRulesError(e?.message || t('templateEditor.rulesFail'));
      })
      .finally(() => {
        if (!cancelled) setCategoryRulesLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, officialMode, t]);

  useEffect(() => {
    if (officialMode) {
      if (!shopType) {
        setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
        setCatalogReferenceShopId('');
        setPublishCurrencies([]);
        return undefined;
      }
      let cancelled = false;
      setMetaLoading(true);
      setPublishCurrencyLoading(false);
      // 官方发货仓选填且不从店铺/参考店仓列表加载；仅拉类目/站点，并记下参考店供制造商/责任人选用
      Promise.all([
        adminAPI.getOfficialTiktokCatalogCategories({ shopType }),
        adminAPI.getOfficialTiktokCatalogSites({ shopType }).catch(() => null),
      ])
        .then(([catRes, siteRes]) => {
          if (cancelled) return;
          const refId = String(catRes?.shopId || siteRes?.shopId || '').trim();
          setCatalogReferenceShopId(refId);
          setMeta({
            categories: catRes?.categories ?? null,
            brands: null,
            ips: null,
            sites: siteRes?.siteList ?? siteRes ?? null,
            warehouses: [],
          });
          setPublishCurrencies([]);
        })
        .catch((e) => {
          if (cancelled) return;
          setCatalogReferenceShopId('');
          setMeta({
            categories: null,
            brands: null,
            ips: null,
            sites: null,
            warehouses: [],
          });
          setPublishCurrencies([]);
          showToast(e?.message || t('templateEditor.categoryLoadFail'), 'error');
        })
        .finally(() => {
          if (!cancelled) setMetaLoading(false);
        });
      return () => { cancelled = true; };
    }
    if (!shopId) {
      setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
      setPublishCurrencies([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setPublishCurrencyLoading(true);
    Promise.all([
      loadShopCategoriesCached(shopId, () => tiktokPlatformAPI.getShopCategories(shopId))
        .then((categories) => ({ categories })),
      tiktokPlatformAPI.getShopBrands(shopId),
      tiktokPlatformAPI.getShopIps(shopId),
      tiktokPlatformAPI.getShopSiteList(shopId),
      tiktokPlatformAPI.getShopWarehouses(shopId, { warehouseType: 'SALES_WAREHOUSE' })
        .catch(() => ({ warehouses: [] })),
      tiktokPlatformAPI.getPublishFillInStandard(shopId, {}).catch(() => null),
    ])
      .then(async ([catRes, brandRes, ipRes, siteRes, warehouseRes, fillInRes]) => {
        if (cancelled) return;
        const warehouses = filterTiktokSalesWarehouses(
          Array.isArray(warehouseRes?.warehouses) ? warehouseRes.warehouses : [],
        );
        const loaded = await loadShopPublishCurrencies({
          shopCaps,
          siteList: siteRes?.siteList ?? null,
          fillInStandard: fillInRes,
          refreshSiteList: () => tiktokPlatformAPI.getShopSiteList(shopId, { refresh: true }),
        });
        if (cancelled) return;
        setMeta({
          categories: catRes?.categories ?? null,
          brands: brandRes?.brands ?? null,
          ips: ipRes?.ips ?? null,
          sites: loaded.siteList,
          warehouses,
        });
        setPublishCurrencies(loaded.currencies);
        const regionFromFillIn = String(fillInRes?.region || '').toUpperCase();
        if (regionFromFillIn) setMarketRegion(regionFromFillIn);
        if (loaded.emptyWarning) {
          // eslint-disable-next-line no-console
          console.warn('[tiktok] publish currency empty', loaded.source, loaded.emptyWarning);
          showToast(loaded.emptyWarning, 'warning');
        }
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('templateEditor.shopOptionsFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setPublishCurrencyLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopCaps.mode, shopType, t]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !form.categoryId) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (officialMode && !shopType) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (!form.productTypeId) {
      setAttrTpl(null);
      setAttrError(t('templateEditor.noProductType'));
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    setAttrError('');
    fetchCategoryAttributeSchema(officialMode, shopId, form.productTypeId, form.categoryId, {
      shopType,
      categoryPathLabel: form.categoryPathLabel,
    })
      .then(async (res) => {
        if (cancelled) return;
        let brandsRaw = null;
        if (shopId && !officialMode) {
          try {
            const brandRes = await tiktokPlatformAPI.getShopBrands(shopId);
            brandsRaw = brandRes?.brands ?? brandRes;
          } catch {
            brandsRaw = null;
          }
        }
        const parsed = applyShopBrandsToAttrTemplate(
          parseAttributeTemplate(res, { productTypeId: form.productTypeId }),
          brandsRaw,
        );
        setAttrTpl(parsed);
        // 材质/成分等控件要 [{valueId,percent}]；编辑回填常是 string / string[]
        // 切类目时按名称把旧类目相似字段回填到新 schema
        const stash = pendingCategoryPreserveRef.current;
        let preserveToast = '';
        if (stash) {
          pendingCategoryPreserveRef.current = null;
          const remappedAttrs = remapProductAttributesAcrossCategories({
            prevAttributes: stash.productAttributes,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSize = remapSizeChartAcrossCategories({
            prevSizeChart: stash.sizeChart,
            prevSelectedSizeIds: stash.selectedSizeIds,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSkc = remapSkcListAcrossCategories({
            prevSkcList: stash.skcList,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const selectedSizeIds = remappedSize.selectedSizeIds.length
            ? remappedSize.selectedSizeIds
            : remappedSkc.selectedSizeIds;
          if (
            remappedAttrs.preservedCount > 0
            || remappedSize.preservedCount > 0
            || remappedSkc.preservedCount > 0
          ) {
            pendingAttrAutoForRef.current = '';
            const parts = [];
            if (remappedAttrs.preservedCount > 0) parts.push(t('templateEditor.keptSimilar', { count: remappedAttrs.preservedCount }));
            if (remappedSkc.preservedCount > 0) parts.push(t('templateEditor.keptMainSpec', { count: remappedSkc.preservedCount }));
            if (remappedSize.preservedCount > 0) parts.push(t('templateEditor.sizeChartWord'));
            preserveToast = parts.length ? t('templateEditor.keptPrefix', { detail: parts.join('、') }) : '';
          }
          setForm((prev) => ({
            ...prev,
            productAttributes: alignAttributesToCategoryTemplate(remappedAttrs.attributes, parsed),
            sizeChart: remappedSize.sizeChart,
            selectedSizeIds,
            skcList: remappedSkc.skcList,
          }));
        } else {
          setForm((prev) => {
            const aligned = alignAttributesToCategoryTemplate(prev.productAttributes, parsed);
            if (aligned === prev.productAttributes) return prev;
            return { ...prev, productAttributes: aligned };
          });
        }
        if (preserveToast) showToast(preserveToast, 'success');
        const total = (parsed.productAttrs?.length || 0)
          + (parsed.skcSaleAttrs?.length || 0)
          + (parsed.skuSaleAttrs?.length || 0)
          + (parsed.sizeAttrs?.length || 0);
        if (total === 0) {
          setAttrError(t('templateEditor.attrEmptyCat'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAttrTpl(null);
        setAttrError(e?.message || t('templateEditor.attrLoadFail'));
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopType, form.categoryId, form.productTypeId, form.categoryPathLabel, t]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !form.productTypeId) {
      setAttrTemplates([]);
      attrTemplatesLoadedForRef.current = '';
      // 切类目时由 onCategoryChange 清空选中；此处勿清，避免编辑回填被冲掉
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    let cancelled = false;
    attrTemplatesLoadedForRef.current = '';
    fetchAttrTemplateList(officialMode, shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
      ...(officialMode ? { shopType } : { scope: templateScope }),
    })
      .then((res) => {
        if (cancelled) return;
        setAttrTemplates(Array.isArray(res?.items) ? res.items : []);
        attrTemplatesLoadedForRef.current = productTypeId;
      })
      .catch(() => {
        if (!cancelled) {
          setAttrTemplates([]);
          attrTemplatesLoadedForRef.current = productTypeId;
        }
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, shopType, form.productTypeId, templateScope]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    tiktokPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(form.categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  // 按末级类目拉取发布规范（quantity_info / package_type 必须带 category_id）
  useEffect(() => {
    if (!form.categoryId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    if (!officialMode && !shopId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    if (officialMode && !shopType) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    const categoryId = String(form.categoryId);
    let cancelled = false;
    setSkuPkgCapsLoading(true);
    const fillInPromise = officialMode
      ? adminAPI.getOfficialTiktokCatalogPublishFillInStandard({
        shopType,
        category_id: Number(categoryId),
        categoryPathLabel: form.categoryPathLabel || '',
        product_type_id: form.productTypeId || undefined,
      }, { shopId: shopId || undefined })
      : tiktokPlatformAPI.getPublishFillInStandard(shopId, {
        category_id: Number(categoryId),
      });
    fillInPromise
      .then((res) => {
        if (cancelled) return;
        const nextCaps = resolveSkuPackageCapabilities(res);
        setSkuPkgCaps(nextCaps);
        const regionFromFillIn = String(res?.region || '').toUpperCase();
        if (regionFromFillIn) setMarketRegion(regionFromFillIn);
        // 能力关闭时清空残留，避免脏数据进预检 / 误展示
        if (!nextCaps.quantityInfo.show || !nextCaps.packageType.show) {
          setForm((prev) => {
            let changed = false;
            const skcList = (prev.skcList || []).map((skc) => {
              let skcChanged = false;
              const skuRows = (skc.skuRows || []).map((sku) => {
                const patch = {};
                if (!nextCaps.quantityInfo.show) {
                  if (sku.quantity != null && String(sku.quantity).trim() !== '') {
                    patch.quantity = '';
                  }
                  if (sku.quantityUnit != null && String(sku.quantityUnit) !== '1') {
                    patch.quantityUnit = '1';
                  }
                }
                if (!nextCaps.packageType.show) {
                  if (sku.packageType != null && String(sku.packageType) !== '' && String(sku.packageType) !== '0') {
                    patch.packageType = '';
                  }
                }
                if (!Object.keys(patch).length) return sku;
                skcChanged = true;
                changed = true;
                return { ...sku, ...patch };
              });
              return skcChanged ? { ...skc, skuRows } : skc;
            });
            return changed ? { ...prev, skcList } : prev;
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      })
      .finally(() => {
        if (!cancelled) setSkuPkgCapsLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopType, form.categoryId, form.categoryPathLabel, form.productTypeId]);

  useEffect(() => {
    if (!colorAttr && !sizeAttr) return;
    setForm((prev) => {
      const colorValues = colorAttr?.values || [];
      const sizeValues = sizeAttr?.values || [];
      const colorIds = new Set(colorValues.map((v) => String(v.id)));
      const sizeIds = new Set(sizeValues.map((v) => String(v.id)));
      const defaults = {
        price: prev.supplyPrice || '',
        stock: prev.stockQty || '',
        weight: prev.weightGrams || '',
        netWeight: prev.netWeightGrams || '',
        currency: prev.currency || '',
      };
      // 尺码表纵轴以 SKU 规格为准，禁止合并模板残留的 selectedSizeIds（否则会出现两个 mid-platform）
      const rowSizeIds = deriveSelectedSizeIds(prev.skcList || []).map(String);
      const prevSizeIds = (prev.selectedSizeIds || []).map(String);
      const nextSizeIds = rowSizeIds.length
        ? rowSizeIds
        : prevSizeIds.filter((id) => !sizeValues.length || sizeIds.has(id) || isCustomSpecKey(id));
      const nextSkcList = (prev.skcList || []).map((skc) => {
        const knownColor = !skc.colorValueId || colorIds.has(String(skc.colorValueId));
        const asCustom = Boolean(
          skc.colorIsCustom
          || skc.colorExtraValue
          || (colorValues.length > 0 && skc.colorValueId && !knownColor),
        );
        const colorLabel = asCustom
          ? (skc.colorExtraValue || skc.colorLabel || '')
          : (colorAttr?.values?.find((v) => String(v.id) === String(skc.colorValueId))?.name
            || skc.colorLabel);
        const existingRows = skc.skuRows || [];
        // 编辑多 SKU：保留全部行；仅在新建且用户主动勾选尺码时按 sizeIds 重建
        const keepAllRows = existingRows.length > 1 || !nextSizeIds.length;
        return {
          ...skc,
          colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          colorIsCustom: asCustom,
          colorExtraValue: asCustom
            ? (skc.colorExtraValue || skc.colorLabel || '')
            : (skc.colorExtraValue || ''),
          colorLabel,
          skuRows: keepAllRows
            ? existingRows.map((row) => ({
              ...row,
              sizeAttributeId: row.sizeAttributeId || sizeAttr?.attributeId || '',
            }))
            : buildSkuRowsForSizes(existingRows, nextSizeIds, sizeAttr, defaults),
        };
      }).filter((skc) => (
        skc.colorValueId
        || String(skc.colorExtraValue || '').trim()
        || String(skc.colorLabel || '').trim()
        || (skc.skuRows || []).length > 0
      ));
      const nextState = {
        ...prev,
        sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
        selectedSizeIds: nextSizeIds.length ? nextSizeIds : prev.selectedSizeIds,
        skcList: nextSkcList,
      };
      const sameSizes = JSON.stringify((prev.selectedSizeIds || []).map(String))
        === JSON.stringify((nextState.selectedSizeIds || []).map(String));
      const sameSaleAttr = String(prev.sizeSaleAttributeId || '') === String(nextState.sizeSaleAttributeId || '');
      const sameSkc = prev.skcList === nextSkcList || (
        (prev.skcList || []).length === nextSkcList.length
        && (prev.skcList || []).every((skc, i) => {
          const n = nextSkcList[i];
          return skc === n
            || (
              String(skc.colorAttributeId || '') === String(n.colorAttributeId || '')
              && Boolean(skc.colorIsCustom) === Boolean(n.colorIsCustom)
              && String(skc.colorExtraValue || '') === String(n.colorExtraValue || '')
              && String(skc.colorLabel || '') === String(n.colorLabel || '')
              && (skc.skuRows || []).length === (n.skuRows || []).length
              && (skc.skuRows || []).every((row, j) => (
                String(row.sizeAttributeId || '') === String(n.skuRows[j]?.sizeAttributeId || '')
              ))
            );
        })
      );
      if (sameSizes && sameSaleAttr && sameSkc) return prev;
      return nextState;
    });
  }, [colorAttr?.attributeId, sizeAttr?.attributeId]);

  useEffect(() => {
    if (!form.categoryId || !categoryOptions.length) return;
    const selected = categoryOptions.find((item) => String(item.categoryId) === String(form.categoryId));
    if (!selected) return;
    const needType = !form.productTypeId && selected.productTypeId;
    const needPath = !form.categoryPathLabel && selected.label;
    if (!needType && !needPath) return;
    setForm((prev) => {
      if (String(prev.categoryId) !== String(selected.categoryId)) return prev;
      return {
        ...prev,
        productTypeId: prev.productTypeId || (selected.productTypeId ? String(selected.productTypeId) : ''),
        categoryPathLabel: prev.categoryPathLabel || selected.label || '',
      };
    });
  }, [form.categoryId, form.productTypeId, form.categoryPathLabel, categoryOptions]);

  const onCategoryChange = (categoryId, meta) => {
    const nextCategoryId = categoryId == null || categoryId === '' ? '' : String(categoryId);
    if (String(form.categoryId || '') === nextCategoryId) {
      return;
    }
    const selected = categoryOptions.find((item) => String(item.categoryId) === nextCategoryId);
    const nextProductTypeId = selected?.productTypeId
      ? String(selected.productTypeId)
      : (meta?.productTypeId ? String(meta.productTypeId) : nextCategoryId);
    pendingCategoryPreserveRef.current = {
      productAttributes: pickFilledProductAttributes(form.productAttributes),
      sizeChart: form.sizeChart && typeof form.sizeChart === 'object' ? form.sizeChart : {},
      selectedSizeIds: Array.isArray(form.selectedSizeIds) ? [...form.selectedSizeIds] : [],
      skcList: Array.isArray(form.skcList) ? form.skcList : [],
      prevAttrTpl: attrTplRef.current,
    };
    pendingAttrAutoForRef.current = nextProductTypeId;
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    setRefVersionConflicts({});
    setRefChangePreviews({});
    setForm((prev) => ({
      ...prev,
      categoryId: nextCategoryId,
      productTypeId: nextProductTypeId,
      categoryPathLabel: selected?.label || meta?.label || '',
      productAttributes: {},
      sizeChart: {},
      sizeChartData: null,
      officialSizeChart: null,
      sizeChartImage: null,
      skcList: [],
      selectedSizeIds: [],
    }));
    setAttrsExpanded(false);
    if (!templateId && nextProductTypeId && (officialMode ? shopType : shopId)) {
      const loadPriority = officialMode
        ? adminAPI.listOfficialTiktokProductTemplates({
          productTypeId: nextProductTypeId,
          shopType,
          page: 1,
          pageSize: 100,
        })
        : tiktokPlatformAPI.listProductTemplates(shopId, {
          productTypeId: nextProductTypeId,
          page: 1,
          pageSize: 100,
        });
      loadPriority.then((res) => {
        setTemplateMeta((prev) => ({
          ...prev,
          sortOrder: nextTemplatePriority(res?.items),
        }));
      }).catch(() => {
        /* ignore */
      });
    }
  };

  const onSitesChange = (abbr) => {
    const next = abbr == null || abbr === '' ? '' : String(abbr);
    const selected = next ? [next] : [];
    const first = siteOptions.find((s) => selected.includes(s.siteAbbr));
    setForm((prev) => ({
      ...prev,
      selectedSites: selected,
      mainSite: first?.mainSiteCode || prev.mainSite || 'tiktok',
      // 自运营币种随已选站点由 pickSiteListCurrency 回写；半托管/全托管仍走发布规范
    }));
  };

  const onWarehouseChange = (warehouseId) => {
    const selected = warehouseOptions.find((item) => String(item.value) === String(warehouseId));
    setForm((prev) => ({
      ...prev,
      warehouseId: selected?.value || '',
      warehouseName: selected?.name || '',
    }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`tko-pub-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const buildPublishFormSnapshot = (formSource = form) => {
    const sizeValueLabels = {};
    (sizeAttr?.values || []).forEach((v) => {
      if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
    });
    const skcList = (formSource.skcList || []).map((skc) => ({
      ...skc,
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || '',
      })),
    }));
    const selectedSizeIds = (formSource.selectedSizeIds?.length
      ? formSource.selectedSizeIds
      : deriveSelectedSizeIds(skcList)
    ).map((id) => String(id));
    return {
      ...formSource,
      shopType: formSource.shopType || (officialMode ? shopType : '') || activeShop?.shopType || '',
      skcList,
      selectedSizeIds,
      sizeValueLabels,
      selectedAttrTemplateId: String(selectedAttrTemplateId || '').trim(),
      selectedSizeChartTemplateId: String(selectedSizeChartTemplateId || '').trim(),
      // TTS：剥离 Temu 半托英文名 / 产地字段，原产地走类目属性
      ...(hideTitleEn
        ? { titleEn: '', englishTitle: '' }
        : {}),
      ...(hideProductOrigin
        ? {
          originRegion: '',
          originCountry: '',
          originRegion2Id: '',
          originCertFiles: [],
        }
        : {}),
      // 全托管/本土：模板不落站点/仓/运费，避免套用发品时误传
      ...(skipSiteWarehouseFreight
        ? {
          selectedSites: [],
          warehouseId: '',
          warehouseName: '',
          freightTemplateId: '',
          shipmentLimitSecond: '',
          tiktokSiteIds: [],
          tiktokSiteId: '',
          tiktokFreightTemplateId: '',
        }
        : {}),
      ...(hideListingSites ? { selectedSites: [] } : {}),
      ...(hideShipmentLimit ? { shipmentLimitSecond: '' } : {}),
    };
  };

  const reloadTemplateFromServer = async () => {
    if (!shopId || !templateId) return;
    setReloadingVersion(true);
    try {
      const res = await tiktokPlatformAPI.getProductTemplate(shopId, templateId, {
        scope: isOfficialProductTemplateScope(templateScope) ? 'OFFICIAL' : templateScope,
      });
      const item = res?.item || res || {};
      const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      const formData = rawPayload.form && typeof rawPayload.form === 'object'
        ? rawPayload.form
        : rawPayload;
      setTemplateVersion(parseTemplateVersion(item.version));
      setTemplateMeta({
        name: String(item.name || '').trim(),
        remark: String(item.remark || '').trim(),
        autoRuleEnabled: item.autoRuleEnabled === true || item.autoRuleEnabled === 1,
        sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0,
        styleNo: String(item.styleNo || '').trim(),
      });
      setForm(sanitizeTiktokFormImageSlots(emptyForm({
        ...formData,
        // 表字段为列表/批量更换类目的权威来源，优先于 payload.form 内可能过期的类目
        categoryId: item.categoryId || formData.categoryId || '',
        productTypeId: item.productTypeId || formData.productTypeId || '',
        categoryPathLabel: item.categoryPathLabel || formData.categoryPathLabel || '',
      })));
      setSelectedAttrTemplateId(String(
        rawPayload.selectedAttrTemplateId
        || formData.selectedAttrTemplateId
        || '',
      ).trim());
      setSelectedSizeChartTemplateId(String(
        rawPayload.selectedSizeChartTemplateId
        || formData.selectedSizeChartTemplateId
        || '',
      ).trim());
      setSelectedAttrTemplateVersion(parseTemplateVersion(
        rawPayload.selectedAttrTemplateVersion
        ?? formData.selectedAttrTemplateVersion,
      ));
      setSelectedSizeChartTemplateVersion(parseTemplateVersion(
        rawPayload.selectedSizeChartTemplateVersion
        ?? formData.selectedSizeChartTemplateVersion,
      ));
      setRefVersionConflicts({});
      setVersionConflict(false);
      showToast(t('templateEditor.reloaded'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const clearRefVersionConflict = (type) => {
    const key = conflictFieldKey(type);
    setRefVersionConflicts((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setRefChangePreviews((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildAttrChangePreview = async () => {
    const id = String(selectedAttrTemplateId || '').trim();
    if ((!officialMode && !shopId) || !id) {
      return { title: t('templateEditor.attrUpdated'), changes: [] };
    }
    const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
    const item = res?.item || {};
    const payload = item.payload || {};
    const { patch } = applyProductAttributeTemplatePayload(
      payload,
      (allProductAttrs || []).map((attr) => String(attr.attributeId || '')).filter(Boolean),
    );
    const nextAttrs = alignAttributesToCategoryTemplate(
      { ...patch },
      attrTplRef.current || attrTpl,
    );
    const { changes, count } = diffProductAttributeChanges({
      current: form.productAttributes || {},
      next: nextAttrs,
      attrDefs: [
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.mainAttrs || []),
        ...(attrTpl?.otherAttrs || []),
      ],
    });
    return {
      title: t('templateEditor.attrUpdated'),
      intro: t('templateEditor.attrDiffIntro', { name: item.name || id, count }),
      changes,
      count,
      templateName: item.name || id,
    };
  };

  const buildSizeChartChangePreview = async () => {
    const id = String(selectedSizeChartTemplateId || '').trim();
    if ((!officialMode && !shopId) || !id) {
      return { title: t('templateEditor.sizeUpdated'), changes: [] };
    }
    const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
    const item = res?.item || {};
    const patch = extractSizeChartTemplatePatch(item);
    const { changes, count } = diffSizeChartChanges({
      current: form.sizeChart || {},
      next: patch.sizeChart || {},
      measureAttrs: sizeMeasureAttrs,
    });
    return {
      title: t('templateEditor.sizeUpdated'),
      intro: t('templateEditor.sizeDiffIntro', { name: item.name || id, count }),
      changes,
      count,
      templateName: item.name || id,
    };
  };

  const reloadReferencedTemplateRegion = async (type) => {
    if (!officialMode && !shopId) return;
    setReloadingRefType(type);
    try {
      if (type === TEMPLATE_VERSION_TYPES.attribute) {
        const id = String(selectedAttrTemplateId || '').trim();
        if (!id) return;
        const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
        const item = res?.item || {};
        const patch = extractAttrTemplatePatch(item);
        setForm((prev) => {
          const aligned = alignAttributesToCategoryTemplate(
            { ...(patch.productAttributes || {}) },
            attrTplRef.current || attrTpl,
          );
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(patch.version);
        clearRefVersionConflict(type);
        showToast(t('templateEditor.reloadedAttrs'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
        const id = String(selectedSizeChartTemplateId || '').trim();
        if (!id) return;
        const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
        const patch = extractSizeChartTemplatePatch(res?.item || {});
        if (patch.kind === 'tiktok-official' || patch.officialSizeChart || patch.sizeChartData) {
          setForm((prev) => ({
            ...prev,
            sizeChartData: patch.sizeChartData || prev.sizeChartData || null,
            officialSizeChart: patch.officialSizeChart || {
              mode: 'template',
              sizeChartData: patch.sizeChartData || null,
              selectedBusinessIds: (patch.platformBusinessIds || []).map(String),
            },
            tiktokSizeTemplateIds: (patch.platformBusinessIds || []).map(String),
          }));
        } else {
          applySizeChart(patch.sizeChart || {}, { replace: true });
        }
        setSelectedSizeChartTemplateVersion(patch.version);
        clearRefVersionConflict(type);
        showToast(t('templateEditor.reloadedSize'), 'success');
      }
    } catch (e) {
      if (!officialMode && isMissingTemplateError(e)) {
        if (type === TEMPLATE_VERSION_TYPES.attribute) {
          setSelectedAttrTemplateId('');
          setSelectedAttrTemplateVersion(null);
        } else if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
          setSelectedSizeChartTemplateId('');
          setSelectedSizeChartTemplateVersion(null);
        }
        clearRefVersionConflict(type);
        showToast(
          type === TEMPLATE_VERSION_TYPES.sizeChart
            ? t('templateEditor.refSizeOut')
            : t('templateEditor.refAttrOut'),
          'warning',
        );
        return;
      }
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingRefType('');
    }
  };

  const onSaveProductTemplate = async () => {
    if (savingTemplateLockRef.current || savingTemplate) return;
    if (!officialMode && !shopId) {
      showToast(t('templateEditor.pickShopSave'), 'warning');
      return;
    }
    if (officialMode && !shopType) {
      showToast(t('templateEditor.pickShopTypeShared'), 'warning');
      return;
    }
    const name = String(templateMeta.name || '').trim();
    if (!name) {
      showToast(t('templateEditor.fillName'), 'error');
      setActiveSection('basic');
      return;
    }
    // TikTok 发品无商家货号；款号作站内模板检索/自动规则键，须单独填写
    const styleNo = String(templateMeta.styleNo || '').trim().slice(0, 128);
    if (!styleNo) {
      showToast(t('templateEditor.fillStyle'), 'error');
      setActiveSection('basic');
      return;
    }
    if (!officialMode && (!form.categoryId || !form.productTypeId)) {
      showToast(t('templateEditor.pickCategory'), 'error');
      setActiveSection('basic');
      return;
    }
    // 官方模板放开业务必填：描述/类目属性/尺码等可空，店铺套用发品时再补
    if (!officialMode && !String(form.description || '').trim()) {
      showToast(t('templateEditor.fillDesc'), 'error');
      setActiveSection('basic');
      setFieldErrors((prev) => ({ ...prev, description: t('templateEditor.fillDesc') }));
      return;
    }
    savingTemplateLockRef.current = true;
    setSavingTemplate(true);
    try {
      let nextAttrTemplateId = String(selectedAttrTemplateId || '').trim();
      let nextAttrTemplateVersion = selectedAttrTemplateVersion;
      let nextSizeChartTemplateId = String(selectedSizeChartTemplateId || '').trim();
      let nextSizeChartTemplateVersion = selectedSizeChartTemplateVersion;
      if (!officialMode && shopId) {
        // 保存前校验已套用的属性库 / 尺码库版本，避免把脏引用写进产品模板
        const refCheck = await checkTemplateVersions(
          tiktokPlatformAPI,
          shopId,
          collectAppliedTemplateRefs({
            attrTemplateId: nextAttrTemplateId,
            attrTemplateVersion: nextAttrTemplateVersion,
            sizeChartTemplateId: nextSizeChartTemplateId,
            sizeChartTemplateVersion: nextSizeChartTemplateVersion,
          }).filter((ref) => ref.version != null),
        );
        if (!refCheck.ok) {
          const conflicts = refCheck.conflicts || [];
          const { missingLibraryRefs, blocking } = partitionTemplateVersionConflicts(conflicts);
          if (missingLibraryRefs.length) {
            missingLibraryRefs.forEach((c) => {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                nextAttrTemplateId = '';
                nextAttrTemplateVersion = null;
                setSelectedAttrTemplateId('');
                setSelectedAttrTemplateVersion(null);
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                nextSizeChartTemplateId = '';
                nextSizeChartTemplateVersion = null;
                setSelectedSizeChartTemplateId('');
                setSelectedSizeChartTemplateVersion(null);
              }
            });
          }
          if (blocking.length) {
            const nextMarks = {};
            blocking.forEach((c) => {
              const key = conflictFieldKey(c.type);
              if (key) nextMarks[key] = true;
            });
            setRefVersionConflicts(nextMarks);
            const nextPreviews = {};
            await Promise.all(blocking.map(async (c) => {
              try {
                if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                  const preview = await buildAttrChangePreview();
                  nextPreviews.productAttributes = preview;
                } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                  const preview = await buildSizeChartChangePreview();
                  nextPreviews.sizeChart = preview;
                }
              } catch {
                /* ignore preview errors */
              }
            }));
            setRefChangePreviews(nextPreviews);
            const attrCount = nextPreviews.productAttributes?.count;
            const sizeCount = nextPreviews.sizeChart?.count;
            const detailBits = [];
            if (attrCount != null) detailBits.push(t('templateEditor.attrCount', { count: attrCount }));
            if (sizeCount != null) detailBits.push(t('templateEditor.sizeCount', { count: sizeCount }));
            showToast(
              detailBits.length
                ? `${conflictToastMessage(blocking)}${t('templateEditor.conflictDetail', { detail: detailBits.join('，') })}`
                : conflictToastMessage(blocking),
              'warning',
              3000,
            );
            const firstType = blocking[0]?.type;
            if (firstType === TEMPLATE_VERSION_TYPES.sizeChart) {
              setActiveSection('size');
            } else {
              setActiveSection('attrs');
            }
            const firstField = conflictFieldKey(firstType) || 'productAttributes';
            const el = document.querySelector(`[data-field-error="${firstField}"]`);
            if (el?.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
        setRefVersionConflicts({});
        setRefChangePreviews({});
      }

      // 店铺模板：有类目时须等属性模板就绪；官方模板不拦
      if (!officialMode && form.productTypeId && (attrLoading || !attrTpl)) {
        showToast(t('templateEditor.attrsNotReady'), 'warning');
        setActiveSection('attrs');
        return;
      }

      if (sizeChartClassError) {
        showToast(sizeChartClassError, 'error');
        setFieldErrors((prev) => ({ ...prev, sizeChart: sizeChartClassError }));
        setActiveSection('spec');
        const el = document.querySelector('[data-field-error="sizeChart"]');
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (sizeChartRequired && sizeChartShopId) {
        const sizeCheck = officialSizeChartRef.current?.validate?.();
        if (sizeCheck && !sizeCheck.ok) {
          showToast(sizeCheck.message || t('templateEditor.fillSizeChart'), 'error');
          setFieldErrors((prev) => ({ ...prev, sizeChart: sizeCheck.message || t('templateEditor.fillSizeChart') }));
          setActiveSection('spec');
          const el = document.querySelector('[data-field-error="sizeChart"]');
          if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }

      // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
      const formWithPrint = officialMode
        ? form
        : await enrichTiktokFormPrintNames(form);
      const cleanedAttributes = attrTpl
        ? filterProductAttributesToCategory(formWithPrint.productAttributes, attrTpl)
        : (formWithPrint.productAttributes || {});
      // 保存前强制从面板取最新尺码草稿：校验读的是面板，form.officialSizeChart 可能尚未 onDraftChange
      const liveSizeDraft = (sizeChartShopId && officialSizeChartRef.current?.getDraft)
        ? officialSizeChartRef.current.getDraft()
        : null;
      const formToPersist = {
        ...formWithPrint,
        productAttributes: cleanedAttributes,
        ...(liveSizeDraft ? {
          officialSizeChart: liveSizeDraft,
          sizeChartData: liveSizeDraft.sizeChartData || formWithPrint.sizeChartData || null,
        } : {}),
      };
      if (
        formWithPrint !== form
        || cleanedAttributes !== formWithPrint.productAttributes
        || liveSizeDraft
      ) {
        setForm(formToPersist);
      }

      const body = {
        styleNo,
        name,
        remark: String(templateMeta.remark || '').trim() || undefined,
        productTypeId: formToPersist.productTypeId,
        categoryId: formToPersist.categoryId,
        categoryPathLabel: formToPersist.categoryPathLabel || undefined,
        autoRuleEnabled: !!templateMeta.autoRuleEnabled,
        sortOrder: Number.isFinite(Number(templateMeta.sortOrder)) ? Number(templateMeta.sortOrder) : 0,
        payload: {
          // 落库前剥掉 blob:/data:，只留 canonical /uploads/…，隔日打开才不会灰块
          form: persistTiktokFormImageSlots({
            ...buildPublishFormSnapshot(formToPersist),
            selectedAttrTemplateId: nextAttrTemplateId,
            selectedSizeChartTemplateId: nextSizeChartTemplateId,
          }),
          selectedAttrTemplateId: nextAttrTemplateId,
          selectedSizeChartTemplateId: nextSizeChartTemplateId,
          selectedAttrTemplateVersion: nextAttrTemplateVersion,
          selectedSizeChartTemplateVersion: nextSizeChartTemplateVersion,
        },
      };
      if (officialMode && shopType) {
        body.shopType = shopType;
      }
      if (templateId && templateVersion != null) {
        body.version = templateVersion;
      }
      if (!templateId && !officialMode) {
        body.scope = templateScope;
      }
      const res = officialMode
        ? (templateId
          ? await adminAPI.updateOfficialTiktokProductTemplate(templateId, body)
          : await adminAPI.createOfficialTiktokProductTemplate(body))
        : (templateId
          ? await tiktokPlatformAPI.updateProductTemplate(shopId, templateId, body)
          : await tiktokPlatformAPI.createProductTemplate(shopId, body));
      const savedItem = officialMode
        ? (res?.item || res?.data?.item || res)
        : (res?.item || res);
      if (savedItem?.id) setTemplateId(String(savedItem.id));
      if (savedItem && parseTemplateVersion(savedItem.version) != null) {
        setTemplateVersion(parseTemplateVersion(savedItem.version));
      }
      setVersionConflict(false);
      setTemplateMeta((prev) => ({ ...prev, styleNo }));
      showToast(templateId ? t('templateEditor.saved') : t('templateEditor.created'), 'success');
      navigate(officialMode && afterSaveNavigate ? afterSaveNavigate : buildTiktokPath('templates/product'));
    } catch (e) {
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'product' }]), 'warning', 3000);
        setVersionConflict(true);
        setActiveSection('basic');
      } else {
        showToast(e?.message || t('templateEditor.saveFail'), 'error');
      }
    } finally {
      savingTemplateLockRef.current = false;
      setSavingTemplate(false);
    }
  };

  const onPublishFromTemplate = async () => {
    if (!shopId) {
      showToast(t('templateEditor.pickShopPublish'), 'warning');
      return;
    }
    try {
      // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
      const formWithPrint = await enrichTiktokFormPrintNames(form);
      if (formWithPrint !== form) {
        setForm(formWithPrint);
      }
      const token = await writeProductTemplatePublishHandoff(
        persistTiktokFormImageSlots(buildPublishFormSnapshot(formWithPrint)),
      );
      if (!token) {
        if (templateId) {
          showToast(t('templateEditor.passFail'), 'warning');
          openTiktokPathInNewTab(
            `products/publish?productTemplateId=${encodeURIComponent(templateId)}`,
          );
          return;
        }
        showToast(t('templateEditor.passNeedSave'), 'error');
        return;
      }
      openTiktokPathInNewTab(
        `products/publish?fromProductTemplate=${encodeURIComponent(token)}`,
      );
    } catch (e) {
      showToast(e?.message || t('templateEditor.openPublishFail'), 'error');
    }
  };

  const { requiredAttrs, optionalAttrs } = useMemo(
    () => partitionProductAttrsForDisplay(attrTpl, null),
    [attrTpl],
  );
  const allProductAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );
  const visibleRequiredAttrs = useMemo(
    () => requiredAttrs.filter((attr) => !isComplianceProductAttr(attr) && isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [requiredAttrs, allProductAttrs, form.productAttributes],
  );
  const visibleOptionalAttrs = useMemo(
    () => optionalAttrs.filter((attr) => !isComplianceProductAttr(attr) && isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [optionalAttrs, allProductAttrs, form.productAttributes],
  );
  const visibleComplianceAttrs = useMemo(
    () => splitComplianceProductAttrs(allProductAttrs).compliance.filter((attr) => isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [allProductAttrs, form.productAttributes],
  );
  const allowedAttrIds = useMemo(
    () => allProductAttrs.map((attr) => String(attr.attributeId || '')).filter(Boolean),
    [allProductAttrs],
  );
  attrTplRef.current = attrTpl;

  const syncTitleEnFromZh = async (zhTitle, { silent = false, force = false } = {}) => {
    const zh = String(zhTitle || '').trim();
    if (!zh) {
      setForm((prev) => (prev.titleEn ? { ...prev, titleEn: '' } : prev));
      lastAutoTitleEnRef.current = '';
      return '';
    }
    const seq = ++titleTranslateSeqRef.current;
    setTitleTranslateBusy(true);
    try {
      const titleEn = await translateTiktokTitleToEnglish(zh, TITLE_MAX_CHARS);
      if (seq !== titleTranslateSeqRef.current) return titleEn;
      setForm((prev) => {
        const currentEn = String(prev.titleEn || '').trim();
        const lastAuto = String(lastAutoTitleEnRef.current || '').trim();
        if (!force && currentEn && currentEn !== lastAuto) {
          return prev;
        }
        lastAutoTitleEnRef.current = titleEn;
        return { ...prev, titleEn };
      });
      return titleEn;
    } catch (error) {
      if (seq !== titleTranslateSeqRef.current) return '';
      if (!silent) {
        showToast(error?.message || t('templateEditor.translateFail'), 'warning');
      }
      throw error;
    } finally {
      if (seq === titleTranslateSeqRef.current) {
        setTitleTranslateBusy(false);
      }
    }
  };

  useEffect(() => {
    if (hideTitleEn) return undefined;
    const zh = String(form.title || '').trim();
    if (!zh) {
      if (form.titleEn) {
        setForm((prev) => ({ ...prev, titleEn: '' }));
      }
      lastAutoTitleEnRef.current = '';
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void syncTitleEnFromZh(zh, { silent: true, force: false }).catch(() => { });
    }, 700);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, hideTitleEn]);

  const handleAiTitle = async () => {
    if (aiTitleBusy || aiDescBusy || savingTemplate) return;
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiTitleDialog'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    setAiTitleBusy(true);
    showToast(t('templateEditor.aiTitleBusy'), 'info');
    try {
      const title = await generateTiktokPublishAiTitle({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
        maxChars: TITLE_MAX_CHARS,
      });
      const en = title.slice(0, TITLE_MAX_CHARS);
      titleTranslateSeqRef.current += 1;
      lastAutoTitleEnRef.current = en;
      setForm((prev) => ({
        ...prev,
        title: en,
        ...(hideTitleEn ? {} : { titleEn: en }),
      }));
      showToast(t('templateEditor.aiTitleDone'), 'success');
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiTitleFail'), 'error');
    } finally {
      setAiTitleBusy(false);
    }
  };

  const handleAiDescription = async () => {
    if (aiDescBusy || aiTitleBusy || savingTemplate) return;
    const mainUrls = resolveAllSkcMainImageUrls(form.skcList);
    if (!mainUrls.length) {
      showToast(t('templateEditor.needMainImage'), 'warning');
      return;
    }
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiDescTitle'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    const brandLabel = brandFieldOptions.find(
      (opt) => String(opt.value) === String(form.brandCode || '').trim(),
    )?.label || String(form.brandCode || '').trim();

    setAiDescBusy(true);
    showToast(t('templateEditor.aiDescBusy', { count: mainUrls.length }), 'info');
    try {
      const html = await generateTiktokPublishAiDescription({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
        brandLabel,
        productTitle: form.title,
        maxChars: TIKTOK_DESCRIPTION_MAX_CHARS,
      });
      setForm((prev) => ({ ...prev, description: html }));
      showToast(t('templateEditor.aiDescDone'), 'success');
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiDescFail'), 'error');
    } finally {
      setAiDescBusy(false);
    }
  };

  const resolvedAttrStyleNo = useMemo(
    () => styleNoFromSupplierCode(
      templateMeta.styleNo || form.supplierCode,
      attrTemplates.map((item) => item?.styleNo).filter(Boolean),
    ),
    [templateMeta.styleNo, form.supplierCode, attrTemplates],
  );

  useEffect(() => {
    const target = String(form.productTypeId || '');
    if (!target || attrLoading || !allowedAttrIds.length) return undefined;
    if (attrTemplatesLoadedForRef.current !== target) return undefined;

    const styleKey = String(resolvedAttrStyleNo || '').trim().toLowerCase();
    const pendingCategory = String(pendingAttrAutoForRef.current || '') === target;
    if (lastAttrAutoStyleKeyRef.current === null) {
      lastAttrAutoStyleKeyRef.current = styleKey;
      if (!pendingCategory) return undefined;
    }
    const styleChanged = styleKey !== lastAttrAutoStyleKeyRef.current;
    if (!pendingCategory && !styleChanged) return undefined;

    const picked = pickAutoRuleTemplate(attrTemplates, { styleNo: resolvedAttrStyleNo });
    pendingAttrAutoForRef.current = '';
    lastAttrAutoStyleKeyRef.current = styleKey;
    if (!picked?.id) return undefined;

    let cancelled = false;
    setSelectedAttrTemplateId(String(picked.id));
    setAttrTemplateBusy(true);
    fetchAttrTemplateDetail(officialMode, shopId, picked.id, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || {};
        const payload = item.payload || {};
        const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
        setForm((prev) => {
          if (String(prev.productTypeId) !== target) return prev;
          // 整表替换：空模板则清空类目属性
          const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current);
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
        const styleHint = resolvedAttrStyleNo ? t('templateEditor.styleHint', { styleNo: resolvedAttrStyleNo }) : '';
        showToast(
          filled
            ? t('templateEditor.autoNamed', { name: picked.name || picked.id, hint: styleHint })
            : t('templateEditor.autoEmpty'),
          'success',
        );
      })
      .catch(() => {
        /* 自动套用失败不打断发品 */
      })
      .finally(() => {
        if (!cancelled) setAttrTemplateBusy(false);
      });
    return () => { cancelled = true; };
  }, [
    shopId,
    officialMode,
    form.productTypeId,
    resolvedAttrStyleNo,
    attrLoading,
    allowedAttrIds,
    attrTemplates,
    templateScope,
    t,
  ]);

  const applyAttrTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if ((!officialMode && !shopId) || !id) return;
    if (!allowedAttrIds.length) {
      showToast(t('templateEditor.attrsNotLoaded'), 'error');
      return;
    }
    setAttrTemplateBusy(true);
    try {
      const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
      const item = res?.item || {};
      const payload = item.payload || {};
      const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
      setForm((prev) => {
        // 整表替换：空模板则清空类目属性
        const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
        return { ...prev, productAttributes: aligned };
      });
      setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
      clearRefVersionConflict(TEMPLATE_VERSION_TYPES.attribute);
      showToast(filled ? t('templateEditor.attrApplied', { count: filled }) : t('templateEditor.attrEmptyCleared'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.attrApplyFail'), 'error');
    } finally {
      setAttrTemplateBusy(false);
    }
  };

  const onAttrTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedAttrTemplateId(id);
    if (id) {
      applyAttrTemplate(id);
      return;
    }
    setSelectedAttrTemplateVersion(null);
    clearRefVersionConflict(TEMPLATE_VERSION_TYPES.attribute);
    // 下拉选空：直接清除类目属性
    setForm((prev) => ({ ...prev, productAttributes: {} }));
  };

  const openAttrSaveModal = () => {
    if (!officialMode && !shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('templateEditor.pickCategory'), 'error');
      return;
    }
    if (!countFilledProductAttributes(form.productAttributes)) {
      showToast(t('templateEditor.fillAttrsFirst'), 'error');
      return;
    }
    setAttrSaveModalOpen(true);
  };

  const refreshAttrTemplates = async () => {
    if (!form.productTypeId || (!officialMode && !shopId)) return;
    try {
      const listRes = await fetchAttrTemplateList(officialMode, shopId, {
        productTypeId: form.productTypeId,
        page: 1,
        pageSize: 50,
        ...(officialMode ? { shopType } : { scope: templateScope }),
      });
      setAttrTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch {
      /* ignore */
    }
  };

  const visibleOptional = useMemo(() => {
    if (attrsExpanded) return visibleOptionalAttrs;
    const hintIds = new Set(Object.keys(indexAttributeChangeHints(
      refChangePreviews.productAttributes,
      !!refVersionConflicts.productAttributes,
    )));
    const head = visibleOptionalAttrs.slice(0, 9);
    const headIds = new Set(head.map((a) => String(a.attributeId)));
    const extraTail = visibleOptionalAttrs
      .slice(9)
      .filter((a) => hintIds.has(String(a.attributeId)))
      .filter((a) => !headIds.has(String(a.attributeId)));
    return extraTail.length ? [...head, ...extraTail] : head;
  }, [
    attrsExpanded,
    visibleOptionalAttrs,
    refChangePreviews.productAttributes,
    refVersionConflicts.productAttributes,
  ]);

  const attrVersionHintById = useMemo(
    () => indexAttributeChangeHints(
      refChangePreviews.productAttributes,
      !!refVersionConflicts.productAttributes,
    ),
    [refChangePreviews.productAttributes, refVersionConflicts.productAttributes],
  );

  if (!officialMode && !tiktokShopCtx) {
    return (
      <div className="tko-wizard tko-publish">
        <p className="tko-muted">{t('templateEditor.openTiktok')}</p>
      </div>
    );
  }

  /** 搜索词 / 卖点计数（条数与合计字符，与 Create Product 官方口径一致） */
  const searchTermList = parseTiktokStringList(form.searchTerms);
  const keyFeatureList = parseTiktokStringList(form.keyProductFeatures);
  /** 预售类型：类目规则允许 ∩ 当前市场支持（US 三种，其余仅 PRE_ORDER） */
  const preSaleTypeOptions = resolveTiktokPreSaleTypes(marketRegion)
    .filter((type) => (categoryRules.allowedSpecialProductTypes || []).includes(type));
  const preSaleRule = resolveTiktokPreSaleRule(marketRegion, form.preSaleType);

  return (
    <div className={`tko-wizard tko-publish${savingTemplate ? ' is-publishing' : ''}`}>
      {savingTemplate ? (
        <div className="tko-publish-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="tko-publish-loading__card">
            <i className="tko-publish-loading__spinner" aria-hidden />
            <strong>{t('templateEditor.saving')}</strong>
            <span>{t('templateEditor.savingHint')}</span>
          </div>
        </div>
      ) : null}
      <aside className="tko-wizard__steps" aria-label={t('templateEditor.sectionsAria')}>
        <div className="tko-wizard__steps-label">
          {t('productTemplate.filePrefix')}
        </div>
        {SECTIONS.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            className={`tko-wizard__step${activeSection === item.id ? ' tko-wizard__step--active' : ''}`}
            onClick={() => scrollToSection(item.id)}
          >
            <span className="tko-wizard__step-index">{idx + 1}</span>
            <span className="tko-wizard__step-title">{fixedUi(t(SECTION_TITLE_KEYS[item.id], { defaultValue: item.title }))}</span>
          </button>
        ))}
        <div className="tko-publish__progress">
          <div className="tko-publish__progress-label">{t('tiktokPublish.progress', { percent: completion })}</div>
          <div className="tko-publish__progress-bar">
            <i style={{ width: `${completion}%` }} />
          </div>
        </div>
      </aside>

      <section className="tko-wizard__panel">
        <div className="tko-wizard__panel-scroll">
          {templateLoading ? <p className="tko-wizard__inline-hint">{t('templateEditor.loading')}</p> : null}
          {fieldErrors.form ? (
            <div className="tko-publish-form-error" role="alert" data-field-error="form">
              {fieldErrors.form}
            </div>
          ) : null}

          {/* —— 基础信息 —— */}
          <div id="tko-pub-basic" className="tko-wizard__block" onFocusCapture={() => setActiveSection('basic')}>
            <h3>{t('tiktokPublish.sectionBasic')}</h3>

            <div className="tko-wizard__form">
              <label className={`tko-field tko-field--full${versionConflict ? ' tko-field--version-conflict' : ''}`}>
                <span className="tko-field__label">
                  {t('productTemplate.colName')} <RequiredMark />
                  <TemplateVersionConflictMark
                    active={versionConflict}
                    label={t('photoLib.updatedMark')}
                    reloading={reloadingVersion}
                    onReload={reloadTemplateFromServer}
                  />
                </span>
                <input
                  className="tko-input"
                  value={templateMeta.name}
                  disabled={templateLoading || savingTemplate}
                  maxLength={128}
                  placeholder={t('templateEditor.namePh')}
                  onChange={(e) => setTemplateMeta((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>
              <label className="tko-field tko-field--full">
                <span className="tko-field__label">
                  {t('productTemplate.colStyleNo')} <RequiredMark />
                </span>
                <input
                  className="tko-input"
                  value={templateMeta.styleNo}
                  disabled={templateLoading || savingTemplate}
                  maxLength={128}
                  placeholder={t('templateEditor.stylePh')}
                  onChange={(e) => setTemplateMeta((prev) => ({ ...prev, styleNo: e.target.value }))}
                />
              </label>
              <label className="tko-field tko-field--full">
                <span className="tko-field__label">{t('productTemplate.colRemark')}</span>
                <input
                  className="tko-input"
                  value={templateMeta.remark}
                  disabled={templateLoading || savingTemplate}
                  maxLength={200}
                  placeholder={t('templateEditor.remarkPh')}
                  onChange={(e) => setTemplateMeta((prev) => ({ ...prev, remark: e.target.value }))}
                />
              </label>
              <div className="tko-field tko-field--full tko-template-auto-rule">
                <div className="tko-template-auto-rule__switch">
                  <TiktokSwitch
                    checked={!!templateMeta.autoRuleEnabled}
                    disabled={templateLoading || savingTemplate}
                    ariaLabel={t('productTemplate.colAutoRule')}
                    onChange={(next) => setTemplateMeta((prev) => ({ ...prev, autoRuleEnabled: next }))}
                  />
                  <span>{t('productTemplate.colAutoRule')}</span>
                </div>
                <p className="tko-wizard__inline-hint">
                  {t('templateEditor.autoHint')}
                </p>
                <label className="tko-field">
                  <span className="tko-field__label">{t('productTemplate.colPriority')}</span>
                  <input
                    className="tko-input"
                    type="number"
                    step={1}
                    value={templateMeta.sortOrder}
                    disabled={templateLoading || savingTemplate}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setTemplateMeta((prev) => ({
                        ...prev,
                        sortOrder: Number.isFinite(next) ? next : 0,
                      }));
                    }}
                  />
                </label>
              </div>

              <label className={`tko-field tko-field--full${fieldErrors.categoryId ? ' tko-field--invalid' : ''}`} data-field-error="categoryId">
                <span className="tko-field__label">
                  {t('publishBasic.category')}
                  {!officialMode ? <RequiredMark /> : null}
                </span>
                <CategoryCascaderSelect
                  shopId={officialMode ? undefined : shopId}
                  officialShopType={officialMode ? shopType : undefined}
                  categories={meta.categories}
                  value={form.categoryId}
                  label={form.categoryPathLabel}
                  disabled={(!officialMode && !shopId) || metaLoading || templateLoading || savingTemplate}
                  placeholder={
                    officialMode
                      ? (metaLoading ? t('templateEditor.loadingCats') : t('publishBasic.pickCategory'))
                      : (!shopId ? t('returnList.pickShop') : t('publishBasic.pickCategory'))
                  }
                  onChange={onCategoryChange}
                />

                <FieldErrorText message={fieldErrors.categoryId} />
              </label>

              <label className={`tko-field tko-field--full${fieldErrors.title ? ' tko-field--invalid' : ''}`} data-field-error="title">
                <span className="tko-field__label tko-field__label-row">
                  <span>
                    {t('publishBasic.productName')}
                    {!officialMode ? <RequiredMark /> : null}
                  </span>
                  <button
                    type="button"
                    className="tko-btn tko-btn--danger tko-btn--sm"
                    disabled={aiTitleBusy || aiDescBusy || templateLoading || savingTemplate || attrLoading}
                    title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                    onClick={() => { void handleAiTitle(); }}
                  >
                    {aiTitleBusy ? t('publishBasic.generating') : t('publishBasic.aiTitle')}
                  </button>
                </span>
                <TitleFieldLoadingShell
                  loading={aiTitleBusy}
                  label={t('publishBasic.aiTitleLoadingTt')}
                >
                  <textarea
                    className="tko-textarea tko-textarea--title"
                    value={form.title}
                    maxLength={TITLE_MAX_CHARS}
                    rows={2}
                    readOnly={aiTitleBusy || templateLoading || savingTemplate}
                    onChange={(e) => update('title', e.target.value)}
                    placeholder={t('publishBasic.titlePhTt', { max: TITLE_MAX_CHARS })}
                  />
                </TitleFieldLoadingShell>
                <span className="tko-publish__counter">{String(form.title || '').length}/{TITLE_MAX_CHARS}</span>
                <FieldErrorText message={fieldErrors.title} />
              </label>

              {/* TTS Create Product：无独立英文名称；原产地/原产国走类目属性 */}
              <div className={`tko-field tko-field--full${fieldErrors.description ? ' tko-field--invalid' : ''}`} data-field-error="description">
                <span className="tko-field__label tko-field__label-row">
                  <span>
                    {t('publishBasic.desc')}
                    {!officialMode ? <RequiredMark /> : null}
                  </span>
                  <button
                    type="button"
                    className="tko-btn tko-btn--danger tko-btn--sm"
                    disabled={aiDescBusy || aiTitleBusy || templateLoading || savingTemplate || attrLoading}
                    title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                    onClick={() => { void handleAiDescription(); }}
                  >
                    {aiDescBusy ? t('publishBasic.generating') : t('publishBasic.aiDesc')}
                  </button>
                </span>
                <TitleFieldLoadingShell
                  loading={aiDescBusy}
                  label={t('publishBasic.aiDescLoading')}
                >
                  <TiktokDescriptionRichEditor
                    value={form.description}
                    shopId={shopId}
                    disabled={templateLoading || savingTemplate || aiDescBusy}
                    maxLength={TIKTOK_DESCRIPTION_MAX_CHARS}
                    placeholder={t('publishBasic.descPhTt')}
                    onChange={(html) => update('description', html)}
                  />
                </TitleFieldLoadingShell>
                <FieldErrorText message={fieldErrors.description} />
              </div>

              <div className={`tko-field tko-field--full${fieldErrors.searchTerms ? ' tko-field--invalid' : ''}`} data-field-error="searchTerms">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('publishBasic.searchTerms')}
                  <FieldHelpMark text={t('publishBasic.searchHelp')} />
                </span>
                <textarea
                  className="tko-textarea"
                  rows={3}
                  value={form.searchTerms || ''}
                  disabled={templateLoading || savingTemplate}
                  onChange={(e) => update('searchTerms', e.target.value)}
                  placeholder={t('publishBasic.searchPh')}
                />
                <span className="tko-publish__counter">
                  {searchTermList.length}/{TIKTOK_SEARCH_TERMS_MAX}
                  {' · '}
                  {tiktokStringListChars(searchTermList)}/{TIKTOK_SEARCH_TERMS_MAX_CHARS}
                </span>
                <FieldErrorText message={fieldErrors.searchTerms} />
              </div>

              <div className={`tko-field tko-field--full${fieldErrors.keyProductFeatures ? ' tko-field--invalid' : ''}`} data-field-error="keyProductFeatures">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('publishBasic.features')}
                  <FieldHelpMark text={t('publishBasic.featuresHelp')} />
                </span>
                <textarea
                  className="tko-textarea"
                  rows={3}
                  value={form.keyProductFeatures || ''}
                  disabled={templateLoading || savingTemplate}
                  onChange={(e) => update('keyProductFeatures', e.target.value)}
                  placeholder={t('publishBasic.featuresPh')}
                />
                <span className="tko-publish__counter">
                  {keyFeatureList.length}/{TIKTOK_KEY_FEATURES_MAX}
                  {' · '}
                  {tiktokStringListChars(keyFeatureList)}/{TIKTOK_KEY_FEATURES_MAX_CHARS}
                </span>
                <FieldErrorText message={fieldErrors.keyProductFeatures} />
              </div>

              <label className={`tko-field${fieldErrors.supplierCode ? ' tko-field--invalid' : ''}`} data-field-error="supplierCode">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('tiktokCatalog.supplier')}
                  <FieldHelpMark text={t('templateEditor.supplierHelpTpl')} />
                </span>
                <input
                  className="tko-input"
                  value={form.supplierCode || ''}
                  maxLength={TIKTOK_EXTERNAL_PRODUCT_ID_MAX}
                  disabled={templateLoading || savingTemplate}
                  onChange={(e) => update('supplierCode', e.target.value)}
                  placeholder={t('publishBasic.supplierPh')}
                />
                <FieldErrorText message={fieldErrors.supplierCode} />
              </label>

              <label className={`tko-field tko-field--full${fieldErrors.brandCode ? ' tko-field--invalid' : ''}`} data-field-error="brandCode">
                <span className="tko-field__label">{t('publishBasic.brand')}</span>
                {officialMode ? (
                  <p className="tko-wizard__inline-hint">
                    {t('templateEditor.officialNoBrand')}
                  </p>
                ) : (
                  <>
                    <SearchableSelect
                      value={form.brandCode || ''}
                      placeholder={t('publishBasic.brandPh')}
                      allowClear
                      filterLocal={false}
                      loading={brandOptionsLoading}
                      emptyText={brandLibraryEmpty ? t('publishBasic.brandEmpty') : t('publishBasic.brandNone')}
                      onSearch={setBrandKeyword}
                      onChange={(v) => update('brandCode', v || '')}
                      options={brandFieldOptions}
                    />
                    {brandLibraryEmpty ? (
                      <span className="tko-wizard__inline-hint">
                        {t('publishBasic.brandEmptyHint')}
                      </span>
                    ) : null}
                  </>
                )}
                <FieldErrorText message={fieldErrors.brandCode} />
              </label>
            </div>
          </div>

          {/* —— 类目属性 —— */}
          <div
            id="tko-pub-attrs"
            className="tko-wizard__block"
            onFocusCapture={() => setActiveSection('attrs')}
            data-field-error="productAttributes"
          >
            <div className="tko-publish__block-head">
              <h3>{t('tiktokPublish.sectionAttrs')}</h3>
            </div>
            <FieldErrorText message={fieldErrors.productAttributes} />
            {!form.categoryId ? (
              <p className="tko-wizard__inline-hint">{t('publishAttr.pickCategory')}</p>
            ) : attrLoading ? (
              <div className="tko-attr-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="tko-attr-loading__banner">
                  <span className="tko-attr-loading__spinner" aria-hidden />
                  <div className="tko-attr-loading__copy">
                    <strong>{t('publishAttr.loadingTitle')}</strong>
                    <span>{t('publishAttr.loadingHint')}</span>
                  </div>
                </div>
                <div className="tko-wizard__form tko-wizard__form--attrs tko-attr-loading__skeleton">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="tko-attr-loading__card">
                      <span className="tko-attr-loading__line tko-attr-loading__line--label" />
                      <span className="tko-attr-loading__line tko-attr-loading__line--field" />
                    </div>
                  ))}
                </div>
              </div>
            ) : attrError && !(visibleRequiredAttrs.length || visibleOptionalAttrs.length || visibleComplianceAttrs.length) ? (
              <p className="tko-wizard__inline-error" role="alert">{attrError}</p>
            ) : (
              <>
                {attrError ? <p className="tko-wizard__inline-hint">{attrError}</p> : null}
                {allProductAttrs.length > 0 ? (
                  <div className={`tko-size-chart-template-bar tko-attr-template-bar${refVersionConflicts.productAttributes ? ' tko-attr-template-bar--version-conflict' : ''}`}>
                    <TemplateVersionConflictMark
                      active={!!refVersionConflicts.productAttributes}
                      label={
                        refChangePreviews.productAttributes?.count != null
                          ? t('publishAttr.updatedCount', { count: refChangePreviews.productAttributes.count })
                          : t('photoLib.updatedMark')
                      }
                      reloading={reloadingRefType === TEMPLATE_VERSION_TYPES.attribute}
                      resolveChangePreview={async () => (
                        refChangePreviews.productAttributes
                        || buildAttrChangePreview()
                      )}
                      onReload={() => reloadReferencedTemplateRegion(TEMPLATE_VERSION_TYPES.attribute)}
                    />
                    <select
                      className="tko-select"
                      value={selectedAttrTemplateId}
                      disabled={attrTemplateBusy || (!officialMode && !shopId)}
                      onChange={(e) => onAttrTemplateSelect(e.target.value)}
                    >
                      <option value="">{t('publishAttr.pickLib')}</option>
                      {attrTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.filledAttrCount != null ? t('publishAttr.filledCount', { count: item.filledAttrCount }) : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="tko-btn tko-btn--primary tko-btn--sm"
                      disabled={attrTemplateBusy || (!officialMode && !shopId) || !form.categoryId}
                      onClick={openAttrSaveModal}
                    >
                      {t('shop.save')}
                    </button>
                  </div>
                ) : null}
                {visibleRequiredAttrs.length > 0 ? (
                  <>
                    <h4 className="tko-publish__subhead">{t('publishAttr.keyAttrs')}</h4>
                    <p className="tko-wizard__inline-hint">
                      {officialMode
                        ? t('templateEditor.officialKeyHint')
                        : t('publishAttr.keyHint')}
                    </p>
                    <div className="tko-wizard__form tko-wizard__form--attrs">
                      {layoutAttributeFields(visibleRequiredAttrs).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          required={!officialMode && isEditorAttrRequiredMark(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)] && attr.isCustomizable === true}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {visibleOptionalAttrs.length > 0 ? (
                  <>
                    <h4 className="tko-publish__subhead">{t('publishAttr.options')}</h4>
                    <div className="tko-wizard__form tko-wizard__form--attrs">
                      {layoutAttributeFields(visibleOptional).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)] && attr.isCustomizable === true}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                        />
                      ))}
                    </div>
                    {visibleOptionalAttrs.length > 9 ? (
                      <div className="tko-publish__expand">
                        <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={() => setAttrsExpanded((v) => !v)}>
                          {attrsExpanded ? t('returnDetail.collapse') : t('publishAttr.expand', { count: visibleOptionalAttrs.length - 9 })}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {!visibleRequiredAttrs.length && !visibleOptionalAttrs.length && !visibleComplianceAttrs.length ? (
                  <p className="tko-wizard__inline-hint">{t('publishAttr.none')}</p>
                ) : null}
              </>
            )}
          </div>

          {/* —— 商品合规（与发品页一致：Get Category Rules + 合规类类目属性） —— */}
          <TiktokComplianceRulePanel
            shopId={complianceShopId}
            region={marketRegion}
            forceShowEuEntities={officialMode}
            rules={categoryRules}
            loading={categoryRulesLoading}
            error={categoryRulesError}
            value={form}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            disabled={templateLoading || savingTemplate}
            onActivate={() => setActiveSection('compliance')}
            extraGroups={visibleComplianceAttrs.length > 0 ? (
              <div className="tko-compliance__group" data-field-error="productAttributes">
                <h4 className="tko-publish__subhead">{t('tiktokPublish.sectionCompliance')}</h4>
                <div className="tko-wizard__form tko-wizard__form--attrs">
                  {layoutAttributeFields(visibleComplianceAttrs).map(({ attr, fullWidth }) => (
                    <AttributeField
                      key={attr.attributeId}
                      attr={attr}
                      required={!officialMode && isEditorAttrRequiredMark(
                        attr,
                        form.productAttributes || {},
                        allProductAttrs,
                      )}
                      fullWidth={fullWidth}
                      shopId={shopId}
                      categoryId={form.categoryId}
                      allowCustomValue={!!customAttrPerm[String(attr.attributeId)] && attr.isCustomizable === true}
                      value={form.productAttributes[attr.attributeId]}
                      onChange={(v) => setAttrValue(attr.attributeId, v)}
                      onCustomValueAdded={appendCustomAttrValue}
                      versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          />

          {/* —— 规格供应 —— */}
          <div id="tko-pub-spec" className="tko-wizard__block" onFocusCapture={() => setActiveSection('spec')} data-field-error="skcList">
            <h3>{t('tiktokPublish.sectionSpecTitle')}</h3>
            <FieldErrorText message={fieldErrors.skcList || fieldErrors.supplierSku || fieldErrors.supply} />
            <SpecSupplySection
              colorAttr={colorAttr}
              sizeAttr={sizeAttr}
              skcList={form.skcList}
              selectedSizeIds={form.selectedSizeIds || []}
              shopId={shopId}
              skipTiktokConvert={officialMode}
              hideRequiredMarks={officialMode}
              categoryId={form.categoryId}
              customAttrPerm={customAttrPerm}
              onCustomValueAdded={appendCustomAttrValue}
              currency={platformCurrency}
              currencyPending={publishCurrencyLoading || !platformCurrency}
              priceMode={priceMode}
              attrLoading={attrLoading}
              defaultSupplierSkuSpu={form.supplierCode || ''}
              onProductSupplierCodeChange={(code) => update('supplierCode', code || '')}
              mainImage={form.mainImage}
              detailImages={form.detailImages || []}
              onMainImageChange={(img) => update('mainImage', img || null)}
              onDetailImagesChange={(imgs) => update(
                'detailImages',
                Array.isArray(imgs) ? imgs : [],
              )}
              printImages={form.printImages || []}
              onPrintImagesChange={updatePrintImages}
              materialImage={form.materialImage}
              materialImgError={fieldErrors.materialImgUrl}
              onMaterialImagesChange={onMaterialImagesChange}
              tiktokVideos={form.tiktokVideos || []}
              videoError={fieldErrors.tiktokVideos}
              onVideosChange={onVideosChange}
              onColorsChange={onColorsChange}
              onSizesChange={onSizesChange}
              onUpdateSkc={updateSkc}
              onUpdateSku={updateSku}
              onRemoveSku={removeSku}
              onBatchUpdateSku={batchUpdateSku}
              afterMaterialContent={(form.categoryId && sizeChartShopId && !sizeChartClassLoading) ? (
                <>
                  {!hideGoodsModels ? (
                    <TiktokModelSelectBar
                      shopId={shopId}
                      visible
                      value={form.goodsModels || []}
                      onChange={(next) => update('goodsModels', next)}
                      errorMessage={fieldErrors.goodsModels}
                      sizeOptions={modelTryOnSizeOptions}
                    />
                  ) : null}
                  <TiktokOfficialSizeChartPanel
                    key={`official-size-${officialMode ? shopType : sizeChartShopId}-${form.categoryId}-${sizeChartClass?.classId || ''}-${templateId || 'new'}`}
                    ref={officialSizeChartRef}
                    shopId={sizeChartShopId}
                    officialMode={officialMode}
                    shopType={shopType}
                    categoryId={form.categoryId}
                    productTypeId={form.productTypeId}
                    categoryPathLabel={form.categoryPathLabel}
                    selectedSizeIds={form.selectedSizeIds}
                    selectedSizeLabels={selectedSizeLabels}
                    defaultStyleNo={resolvedAttrStyleNo}
                    required={sizeChartRequired}
                    sizeClass={sizeChartClass}
                    loading={sizeChartClassLoading}
                    errorMessage={fieldErrors.sizeChart}
                    initialDraft={form.officialSizeChart}
                    onDraftChange={onOfficialSizeChartDraftChange}
                    sizeChartImage={form.sizeChartImage || null}
                    onSizeChartImageChange={(img) => update('sizeChartImage', img || null)}
                    skipTiktokConvert={officialMode}
                  />
                </>
              ) : (sizeChartRequired || sizeChartClassLoading || sizeChartClassError) ? (
                <>
                  {!hideGoodsModels ? (
                    <TiktokModelSelectBar
                      shopId={shopId}
                      visible
                      value={form.goodsModels || []}
                      onChange={(next) => update('goodsModels', next)}
                      errorMessage={fieldErrors.goodsModels}
                      sizeOptions={modelTryOnSizeOptions}
                    />
                  ) : null}
                  {sizeChartClassError ? (
                    <p className="tko-wizard__inline-error" role="alert">{sizeChartClassError}</p>
                  ) : null}
                  {sizeChartClassLoading ? (
                    <p className="tko-wizard__hint" aria-live="polite">{t('templateEditor.sizeChartLoading')}</p>
                  ) : null}
                </>
              ) : null}
            />
          </div>

          {!hideGuideFiles ? (
            <div className="tko-wizard__block" onFocusCapture={() => setActiveSection('spec')}>
              <TiktokGuideFilePanel
                shopId={officialMode ? '' : shopId}
                files={form.guideFiles || []}
                onChange={(next) => update('guideFiles', next)}
                errorMessage={fieldErrors.guideFiles}
              />
            </div>
          ) : null}

          {/* —— 包装信息 —— */}
          <div
            id="tko-pub-size"
            className="tko-wizard__block"
            onFocusCapture={() => setActiveSection('size')}
          >
            <h3>{t('tiktokPublish.sectionSize')}</h3>

            {ttsPublishMode ? (
              <div
                className={`tko-product-package-row${fieldErrors.weightGrams || fieldErrors.pkgLength ? ' is-invalid' : ''}`}
                data-field-error={fieldErrors.weightGrams ? 'weightGrams' : 'pkgLength'}
              >
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label tko-field__label-with-help">
                    {t('publishPkg.pkgWeight')}{!officialMode ? <RequiredMark /> : null}
                    <FieldHelpMark text={t('publishPkg.pkgWeightHelp')} />
                  </span>
                  <span className="tko-pkg-inline tko-pkg-inline--weight">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.weightGrams || ''}
                      inputMode="decimal"
                      disabled={templateLoading || savingTemplate}
                      onChange={(e) => updateProductPackage({
                        weightGrams: e.target.value.replace(/[^\d.]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_WEIGHT_UNIT}</span>
                  </span>
                </label>
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label">{t('publishPkg.len')}{!officialMode ? <RequiredMark /> : null}</span>
                  <span className="tko-pkg-inline">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.pkgLength ?? ''}
                      inputMode="numeric"
                      disabled={templateLoading || savingTemplate}
                      onChange={(e) => updateProductPackage({
                        pkgLength: e.target.value.replace(/[^\d]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_DIM_UNIT}</span>
                  </span>
                </label>
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label">{t('publishPkg.wid')}{!officialMode ? <RequiredMark /> : null}</span>
                  <span className="tko-pkg-inline">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.pkgWidth ?? ''}
                      inputMode="numeric"
                      disabled={templateLoading || savingTemplate}
                      onChange={(e) => updateProductPackage({
                        pkgWidth: e.target.value.replace(/[^\d]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_DIM_UNIT}</span>
                  </span>
                </label>
                <label className="tko-product-package-row__item">
                  <span className="tko-field__label">{t('publishPkg.hei')}{!officialMode ? <RequiredMark /> : null}</span>
                  <span className="tko-pkg-inline">
                    <input
                      className="tko-input tko-pkg-inline__input"
                      value={form.pkgHeight ?? ''}
                      inputMode="numeric"
                      disabled={templateLoading || savingTemplate}
                      onChange={(e) => updateProductPackage({
                        pkgHeight: e.target.value.replace(/[^\d]/g, ''),
                      })}
                      placeholder={t('publishPkg.input')}
                    />
                    <span className="tko-pkg-inline__unit">{TIKTOK_DIM_UNIT}</span>
                  </span>
                </label>
                <FieldErrorText message={fieldErrors.weightGrams || fieldErrors.pkgLength} />
              </div>
            ) : null}

            {!ttsPublishMode ? (
              <>
                {!hideTemuSkuPackageExtras && accessoryLoading ? (
                  <p className="tko-wizard__inline-hint">{t('publishPkg.loadingAcc')}</p>
                ) : null}
                <div className="tko-publish__table-wrap tko-publish__table-wrap--scroll">
                  <table className="tko-publish__table tko-pkg-table">
                    <colgroup>
                      <col className="tko-pkg-col tko-pkg-col--color" />
                      <col className="tko-pkg-col tko-pkg-col--size" />
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--sensitive" /> : null}
                      <col className="tko-pkg-col tko-pkg-col--dims" />
                      <col className="tko-pkg-col tko-pkg-col--weight" />
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--sku-class" /> : null}
                      {!hideTemuSkuPackageExtras ? <col className="tko-pkg-col tko-pkg-col--accessories" /> : null}
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{colorLabel}</th>
                        <th>{sizeLabel}</th>
                        {!hideTemuSkuPackageExtras ? (
                          <th className="tko-pkg-table__sensitive">
                            <span className="tko-supply-th">
                              <span className="tko-th-with-help">
                                {t('publishPkg.sensitive')}{!officialMode ? <RequiredMark /> : null}
                              </span>
                              <button
                                type="button"
                                className="tko-supply-th__batch"
                                onClick={() => setPkgBatchMode('sensitive')}
                              >
                                {t('publishSpec.batch')}
                              </button>
                            </span>
                          </th>
                        ) : null}
                        <th className="tko-pkg-table__dims">
                          <span className="tko-supply-th">
                            <span className="tko-th-with-help">
                              {t('publishPkg.volume', { unit: TIKTOK_DIM_UNIT })}{!officialMode ? <RequiredMark /> : null}
                              <FieldHelpMark text={t('publishPkg.volumeHelpTt')} />
                            </span>
                            <button
                              type="button"
                              className="tko-supply-th__batch"
                              onClick={() => setPkgBatchMode('dims')}
                            >
                              {t('publishSpec.batch')}
                            </button>
                          </span>
                        </th>
                        <th className="tko-pkg-table__weight">
                          <span className="tko-supply-th">
                            <span>{t('publishPkg.weight')}{!officialMode ? <RequiredMark /> : null}</span>
                            <button
                              type="button"
                              className="tko-supply-th__batch"
                              onClick={() => setPkgBatchMode('weight')}
                            >
                              {t('publishSpec.batch')}
                            </button>
                          </span>
                        </th>
                        {!hideTemuSkuPackageExtras ? (
                          <>
                            <th className="tko-pkg-table__sku-class">
                              <span className="tko-supply-th">
                                <span className="tko-th-with-help">
                                  {t('publishPkg.skuClass')}
                                  <FieldHelpMark text={t('publishPkg.skuClassHelpTt')} />
                                </span>
                                <button
                                  type="button"
                                  className="tko-supply-th__batch"
                                  onClick={() => setPkgBatchMode('quantity')}
                                >
                                  {t('publishSpec.batch')}
                                </button>
                              </span>
                            </th>
                            <th className="tko-pkg-table__accessories">
                              <span className="tko-supply-th">
                                <span className="tko-th-with-help">
                                  {t('publishPkg.accessories')}
                                  <FieldHelpMark text={t('publishPkg.accHelp')} />
                                </span>
                                <button
                                  type="button"
                                  className="tko-supply-th__batch"
                                  onClick={() => setPkgBatchMode('accessories')}
                                  disabled={accessoryLoading || !accessoryOptions.length}
                                >
                                  {t('publishSpec.batch')}
                                </button>
                              </span>
                              {accessoryLoadError ? (
                                <div className="tko-wizard__inline-hint" role="status">
                                  {accessoryLoading ? t('publishPkg.loadingAcc') : accessoryLoadError}
                                  {!accessoryLoading && accessoryLoadError ? (
                                    <button
                                      type="button"
                                      className="tko-supply-th__batch"
                                      style={{ marginLeft: 8 }}
                                      onClick={() => setAccessoryReloadKey((k) => k + 1)}
                                    >
                                      {t('commerce.retry')}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </th>
                          </>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {form.skcList.flatMap((skc) => {
                        const rows = skc.skuRows?.length ? skc.skuRows : [];
                        return rows.map((sku, rowIndex) => {
                          const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                            ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                            : (
                              attributeValueDisplayLabel(
                                sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))
                                || sku.sizeLabel,
                              ) || '—'
                            );
                          const skuClass = String(sku.skuClassification || '1');
                          const isMulti = skuClass === '2' || skuClass === '3';
                          const sensitiveOn = Number(sku.isSensitive || 0) === 1;
                          const sensitiveSlots = sensitiveSlotsFromList(sku.sensitiveList, 3);
                          const pieceUnit = String(sku.pieceUnitCode || sku.quantityUnit || '1');
                          const pieceCount = sku.numberOfPieces ?? sku.quantity ?? '1';
                          const accessories = Array.isArray(sku.accessories) && sku.accessories.length
                            ? sku.accessories
                            : [createEmptyAccessoryRow()];
                          const filledAccessories = accessories.filter(
                            (row) => Number(row?.vid) > 0 && Number(row?.num) > 0,
                          );
                          const needAccessories = skuClass === '3';
                          return (
                            <tr key={`${skc.id}-${sku.id}`}>
                              {rowIndex === 0 ? (
                                <td rowSpan={rows.length} className="tko-pkg-table__color">
                                  {skc.colorLabel || '—'}
                                </td>
                              ) : null}
                              <td className="tko-pkg-table__size">{sizeName}</td>
                              {!hideTemuSkuPackageExtras ? (
                                <td className="tko-pkg-table__sensitive">
                                  <div className="tko-pkg-sensitive">
                                    <SearchableSelect
                                      value={String(sku.isSensitive ?? 0)}
                                      allowClear={false}
                                      onChange={(v) => updateSku(skc.id, sku.id, {
                                        isSensitive: Number(v) === 1 ? 1 : 0,
                                        ...(Number(v) !== 1 ? { sensitiveList: [] } : {}),
                                      })}
                                      options={localizeOptions(TIKTOK_SENSITIVE_YES_NO_OPTIONS, YES_NO_KEYS)}
                                    />
                                    {sensitiveOn ? (
                                      <div className="tko-pkg-sensitive__types">
                                        {sensitiveSlots.map((slotVal, slotIdx) => (
                                          <SearchableSelect
                                            key={`sens-${sku.id}-${slotIdx}`}
                                            value={slotVal}
                                            allowClear
                                            placeholder={t('shop.pleaseSelect')}
                                            onChange={(v) => {
                                              const nextSlots = [...sensitiveSlots];
                                              nextSlots[slotIdx] = v == null ? '' : String(v);
                                              updateSku(skc.id, sku.id, {
                                                sensitiveList: sensitiveListFromSlots(nextSlots),
                                              });
                                            }}
                                            options={localizeOptions(TIKTOK_SENSITIVE_LIST_OPTIONS, SENSITIVE_KEYS)}
                                          />
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              ) : null}
                              <td className="tko-pkg-table__dims">
                                <div className="tko-pkg-volume">
                                  <label className="tko-pkg-volume__item">
                                    <span>{t('publishPkg.len')}</span>
                                    <input
                                      className="tko-input"
                                      value={sku.length ?? ''}
                                      inputMode="numeric"
                                      onChange={(e) => updateSku(skc.id, sku.id, { length: e.target.value })}
                                      placeholder={t('publishPkg.input')}
                                    />
                                    <em>{TIKTOK_DIM_UNIT}</em>
                                  </label>
                                  <label className="tko-pkg-volume__item">
                                    <span>{t('publishPkg.wid')}</span>
                                    <input
                                      className="tko-input"
                                      value={sku.width ?? ''}
                                      inputMode="numeric"
                                      onChange={(e) => updateSku(skc.id, sku.id, { width: e.target.value })}
                                      placeholder={t('publishPkg.input')}
                                    />
                                    <em>{TIKTOK_DIM_UNIT}</em>
                                  </label>
                                  <label className="tko-pkg-volume__item">
                                    <span>{t('publishPkg.hei')}</span>
                                    <input
                                      className="tko-input"
                                      value={sku.height ?? ''}
                                      inputMode="numeric"
                                      onChange={(e) => updateSku(skc.id, sku.id, { height: e.target.value })}
                                      placeholder={t('publishPkg.input')}
                                    />
                                    <em>{TIKTOK_DIM_UNIT}</em>
                                  </label>
                                </div>
                              </td>
                              <td className="tko-pkg-table__weight">
                                <div className="tko-pkg-inline tko-pkg-inline--weight">
                                  <input
                                    className="tko-input tko-pkg-inline__input"
                                    value={sku.weight ?? ''}
                                    inputMode="decimal"
                                    onChange={(e) => updateSku(skc.id, sku.id, { weight: e.target.value })}
                                    placeholder={t('publishPkg.input')}
                                  />
                                  <span className="tko-pkg-inline__unit">{TIKTOK_WEIGHT_UNIT}</span>
                                </div>
                              </td>
                              {!hideTemuSkuPackageExtras ? (
                                <>
                                  <td className="tko-pkg-table__sku-class">
                                    <div className="tko-pkg-sku-class">
                                      <SearchableSelect
                                        value={skuClass}
                                        allowClear={false}
                                        onChange={(v) => updateSku(skc.id, sku.id, {
                                          skuClassification: String(v || '1'),
                                          ...(String(v || '1') === '1'
                                            ? { individuallyPacked: '', numberOfPieces: pieceCount || '1', quantity: pieceCount || '1' }
                                            : {}),
                                        })}
                                        options={localizeOptions(TIKTOK_SKU_CLASSIFICATION_OPTIONS, SKU_CLASS_KEYS)}
                                      />
                                      <div className="tko-pkg-sku-class__row">
                                        <span>{t('publishPkg.pieceQty')}</span>
                                        <input
                                          className="tko-input tko-pkg-inline__input--qty"
                                          value={pieceCount}
                                          inputMode="numeric"
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            updateSku(skc.id, sku.id, {
                                              numberOfPieces: val,
                                              quantity: val,
                                            });
                                          }}
                                          placeholder={t('publishPkg.input')}
                                        />
                                        <SearchableSelect
                                          className="tko-select--unit"
                                          value={pieceUnit}
                                          allowClear={false}
                                          onChange={(v) => updateSku(skc.id, sku.id, {
                                            pieceUnitCode: String(v || '1'),
                                            quantityUnit: String(v || '1'),
                                          })}
                                          options={localizeOptions(TIKTOK_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS)}
                                        />
                                      </div>
                                      <div className="tko-pkg-sku-class__row">
                                        <span>{t('publishPkg.totalContains')}</span>
                                        <input
                                          className="tko-input tko-pkg-inline__input--qty"
                                          value={sku.totalContains ?? ''}
                                          inputMode="numeric"
                                          onChange={(e) => updateSku(skc.id, sku.id, {
                                            totalContains: e.target.value,
                                          })}
                                          placeholder={t('publishPkg.input')}
                                        />
                                        <span className="tko-pkg-inline__unit">
                                          {optionLabel(TIKTOK_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS, pieceUnit, t('publishPkg.piece'))}
                                        </span>
                                      </div>
                                      {isMulti ? (
                                        <SearchableSelect
                                          value={sku.individuallyPacked === 0 || sku.individuallyPacked === '0'
                                            ? '0'
                                            : (sku.individuallyPacked === 1 || sku.individuallyPacked === '1' ? '1' : '')}
                                          allowClear
                                          placeholder={t('publishPkg.indivPh')}
                                          onChange={(v) => updateSku(skc.id, sku.id, {
                                            individuallyPacked: v == null || v === '' ? '' : String(v),
                                          })}
                                          options={localizeOptions(TIKTOK_INDIVIDUALLY_PACKED_OPTIONS, PACKED_KEYS)}
                                        />
                                      ) : null}
                                    </div>
                                  </td>
                                  <td className="tko-pkg-table__accessories">
                                    <div className="tko-pkg-accessories">
                                      {accessories.map((item, idx) => (
                                        <div key={`${sku.id}-acc-${idx}`} className="tko-pkg-accessories__row">
                                          <SearchableSelect
                                            value={item.vid ? String(item.vid) : ''}
                                            allowClear
                                            placeholder={
                                              accessoryLoading
                                                ? t('publishPkg.accLoading')
                                                : (accessoryOptions.length ? t('publishPkg.accSearch') : t('publishPkg.accNone'))
                                            }
                                            disabled={accessoryLoading || !accessoryOptions.length}
                                            onChange={(v) => {
                                              const opt = accessoryOptions.find((o) => o.value === String(v));
                                              const next = accessories.map((row, i) => (
                                                i === idx
                                                  ? {
                                                    ...row,
                                                    vid: v ? Number(v) : '',
                                                    label: opt?.label || '',
                                                    unitCode: opt?.unitCode || row.unitCode || 1,
                                                    unitName: opt?.unitName || row.unitName || '件',
                                                    thumbUrl: opt?.thumbUrl || row.thumbUrl || '',
                                                  }
                                                  : row
                                              ));
                                              updateSku(skc.id, sku.id, { accessories: next });
                                            }}
                                            options={accessoryOptions}
                                          />
                                          <input
                                            className="tko-input tko-pkg-inline__input--qty"
                                            value={item.num ?? ''}
                                            inputMode="numeric"
                                            placeholder={t('publishPkg.qty')}
                                            onChange={(e) => {
                                              const next = accessories.map((row, i) => (
                                                i === idx ? { ...row, num: e.target.value } : row
                                              ));
                                              updateSku(skc.id, sku.id, { accessories: next });
                                            }}
                                          />
                                          <span className="tko-pkg-inline__unit">
                                            {item.unitName
                                              || accessoryOptions.find((o) => o.value === String(item.vid))?.unitName
                                              || t('publishPkg.piece')}
                                          </span>
                                          <button
                                            type="button"
                                            className="tko-pkg-accessories__del"
                                            title={t('publishPkg.deleteItem')}
                                            aria-label={t('publishPkg.deleteItem')}
                                            onClick={() => {
                                              const next = accessories.filter((_, i) => i !== idx);
                                              updateSku(skc.id, sku.id, {
                                                accessories: next.length ? next : [createEmptyAccessoryRow()],
                                              });
                                            }}
                                          >
                                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                                              <path
                                                fill="currentColor"
                                                d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9zm-1 13h12a1 1 0 0 0 1-1V7H5v14a1 1 0 0 0 1 1z"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        className="tko-pkg-accessories__add"
                                        disabled={accessories.length >= 20 || accessoryLoading}
                                        onClick={() => {
                                          updateSku(skc.id, sku.id, {
                                            accessories: [
                                              ...accessories,
                                              createEmptyAccessoryRow(),
                                            ],
                                          });
                                        }}
                                      >
                                        {t('publishPkg.add')}
                                      </button>
                                      {needAccessories && !filledAccessories.length ? (
                                        <span className="tko-wizard__inline-hint">{t('publishPkg.mixNeed')}</span>
                                      ) : null}
                                    </div>
                                  </td>
                                </>
                              ) : null}
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>

                {!hideOuterPackage ? (
                  <>
                    <h4 className="tko-publish__subhead">{t('publishPkg.productPkg')}</h4>

                    <div className="tko-wizard__form tko-wizard__form--2">
                      <label className={`tko-field${fieldErrors.packageShape ? ' tko-field--invalid' : ''}`} data-field-error="packageShape">
                        <span className="tko-field__label">{t('publishPkg.outerShape')}{!officialMode ? <RequiredMark /> : null}</span>
                        <SearchableSelect
                          value={form.packageShape || ''}
                          allowClear
                          placeholder={t('shop.pleaseSelect')}
                          onChange={(v) => update('packageShape', v == null ? '' : String(v))}
                          options={localizeOptions(TIKTOK_OUTER_PACKAGE_SHAPE_OPTIONS, OUTER_SHAPE_KEYS)}
                        />
                        {fieldErrors.packageShape ? (
                          <span className="tko-field__error" role="alert">{fieldErrors.packageShape}</span>
                        ) : null}
                      </label>
                      <label className={`tko-field${fieldErrors.packageTypeOuter ? ' tko-field--invalid' : ''}`} data-field-error="packageTypeOuter">
                        <span className="tko-field__label">{t('publishPkg.outerType')}{!officialMode ? <RequiredMark /> : null}</span>
                        <SearchableSelect
                          value={form.packageTypeOuter || ''}
                          allowClear
                          placeholder={t('shop.pleaseSelect')}
                          onChange={(v) => update('packageTypeOuter', v == null ? '' : String(v))}
                          options={localizeOptions(TIKTOK_OUTER_PACKAGE_TYPE_OPTIONS, OUTER_TYPE_KEYS)}
                        />
                        {fieldErrors.packageTypeOuter ? (
                          <span className="tko-field__error" role="alert">{fieldErrors.packageTypeOuter}</span>
                        ) : null}
                      </label>
                    </div>

                    <div
                      className={`tko-field tko-outer-pkg-images${fieldErrors.outerPackageImages ? ' tko-field--invalid' : ''}`}
                      data-field-error="outerPackageImages"
                    >
                      <span className="tko-field__label tko-field__label-with-help">
                        {t('publishPkg.outerImages')}{!officialMode ? <RequiredMark /> : null}
                        <FieldHelpMark text={t('templateEditor.outerHelpTpl')} />
                      </span>
                      <p className="tko-outer-pkg-images__hint">
                        {t('publishPkg.outerHint')}
                      </p>
                      <PublishImageUploadZone
                        compact
                        labelInside
                        label={t('publishPkg.outerImg')}
                        imageType={1}
                        imageBizType={1}
                        multiple
                        maxCount={6}
                        enforceTiktokRules={false}
                        skipTiktokConvert={Boolean(officialMode)}
                        value={Array.isArray(form.outerPackageImages) ? form.outerPackageImages : []}
                        shopId={shopId}
                        onChange={(next) => {
                          update('outerPackageImages', Array.isArray(next) ? next : (next ? [next] : []));
                        }}
                      />
                      {fieldErrors.outerPackageImages ? (
                        <span className="tko-field__error" role="alert">{fieldErrors.outerPackageImages}</span>
                      ) : null}
                    </div>
                  </>
                ) : null}

                {pkgBatchMode ? (
                  <PackageBatchModal
                    mode={pkgBatchMode}
                    skcList={form.skcList}
                    sizeAttr={sizeAttr}
                    accessoryOptions={accessoryOptions}
                    weightUnit={TIKTOK_WEIGHT_UNIT}
                    dimUnit={TIKTOK_DIM_UNIT}
                    onClose={() => setPkgBatchMode(null)}
                    onConfirm={(result) => {
                      if (!result?.applyAll && !result?.bySize) {
                        setPkgBatchMode(null);
                        return;
                      }
                      setForm((prev) => ({
                        ...prev,
                        weightUnit: TIKTOK_WEIGHT_UNIT,
                        dimUnit: TIKTOK_DIM_UNIT,
                        skcList: result.applyAll
                          ? applyPackageBatchToAllSkus(prev.skcList, result.patch)
                          : applyPackageBatchBySize(prev.skcList, result.bySize),
                      }));
                      showToast(
                        result.applyAll
                          ? t('publishPkg.appliedAll')
                          : t('publishPkg.appliedBySizeTt'),
                        'success',
                      );
                      setPkgBatchMode(null);
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </div>

          {/* —— 销售发货：半托管填站点/仓/运费；全托管与本土跳过 —— */}
          <div id="tko-pub-sales" className="tko-wizard__block" onFocusCapture={() => setActiveSection('sales')}>
            <h3>{t('tiktokPublish.salesTitle')}</h3>

            {skipSiteWarehouseFreight ? (
              <p className="tko-wizard__inline-hint">
                {t('templateEditor.salesSkipTpl')}
              </p>
            ) : (
              <div className="tko-wizard__form">
                {!hideListingSites && (isSemiManaged || shopCaps.usesPublishSiteList) ? (
                  <div className={`tko-field tko-field--full${fieldErrors.selectedSites ? ' tko-field--invalid' : ''}`} data-field-error="selectedSites">
                    <span className="tko-field__label">
                      {t('tiktokPublish.listingSites')}
                      {!officialMode ? <RequiredMark /> : null}
                    </span>
                    <SearchableSelect
                      value={(form.selectedSites || [])[0] || ''}
                      onChange={onSitesChange}
                      placeholder={
                        isSemiManaged
                          ? t('tiktokPublish.pickTiktok')
                          : (metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.pickOrSearch'))
                      }
                      disabled={!isSemiManaged && !officialMode && (metaLoading || siteSelectOptions.length === 0)}
                      emptyText={
                        isSemiManaged
                          ? t('tiktokPublish.noSites')
                          : (metaLoading
                            ? t('tiktokPublish.loading')
                            : (siteSelectOptions.length === 0
                              ? (officialMode ? t('templateEditor.sitesSyncAny') : t('tiktokPublish.noSitesYet'))
                              : t('tiktokPublish.noSitesYet')))
                      }
                      options={siteSelectOptions}
                    />
                    <FieldErrorText message={fieldErrors.selectedSites} />
                  </div>
                ) : null}

                <div className={`tko-field tko-field--full${fieldErrors.warehouseId ? ' tko-field--invalid' : ''}`} data-field-error="warehouseId">
                  <span className="tko-field__label">
                    {t('tiktokPublish.warehouse')}
                    {officialMode ? (
                      <span className="tko-field__optional">{t('tiktokPublish.optional')}</span>
                    ) : (multiWarehouse || isSemiManaged ? <RequiredMark /> : null)}
                  </span>
                  {officialMode ? (
                    <>
                      <SearchableSelect
                        value={form.warehouseId}
                        onChange={onWarehouseChange}
                        allowClear
                        placeholder={t('templateEditor.officialWhPh')}
                        emptyText={t('templateEditor.officialWhEmpty')}
                        options={warehouseOptions}
                      />

                    </>
                  ) : metaLoading ? (
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.loadingWarehouses')}</p>
                  ) : warehouseOptions.length === 0 ? (
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.noWarehouses')}</p>
                  ) : (
                    <SearchableSelect
                      value={form.warehouseId}
                      onChange={onWarehouseChange}
                      allowClear={false}
                      placeholder={t('tiktokPublish.pickWarehouse')}
                      emptyText={t('tiktokPublish.noWarehouseOption')}
                      options={warehouseOptions}
                    />
                  )}
                  <FieldErrorText message={fieldErrors.warehouseId} />
                </div>

                <div className={`tko-field tko-field--full${fieldErrors.freightTemplateId ? ' tko-field--invalid' : ''}`} data-field-error="freightTemplateId">
                  <span className="tko-field__label">
                    {t('tiktokPublish.freight')}
                    <span className="tko-field__optional">{t('tiktokPublish.optional')}</span>
                  </span>
                  {officialMode ? (
                    <>
                      <SearchableSelect
                        value={form.freightTemplateId}
                        onChange={(v) => update('freightTemplateId', v)}
                        allowClear
                        placeholder={t('templateEditor.officialFreightPh')}
                        emptyText={t('templateEditor.officialFreightEmpty')}
                        options={freightOptions}
                        placement="top"
                      />

                    </>
                  ) : freightLoading ? (
                    <p className="tko-wizard__inline-hint">{t('tiktokPublish.loadingFreight')}</p>
                  ) : freightOptions.length === 0 ? (
                    <p className="tko-wizard__inline-hint">
                      {t('templateEditor.noFreightShort')}
                    </p>
                  ) : (
                    <SearchableSelect
                      value={form.freightTemplateId}
                      onChange={(v) => update('freightTemplateId', v)}
                      allowClear
                      placeholder={t('tiktokPublish.pickFreight')}
                      options={freightOptions}
                      placement="top"
                    />
                  )}
                  <FieldErrorText message={fieldErrors.freightTemplateId} />
                </div>

                {!hideShipmentLimit ? (
                  <div className="tko-field tko-field--full">
                    <span className="tko-field__label">
                      {t('tiktokPublish.shipLimit')}{isSemiManaged ? <RequiredMark /> : null}
                    </span>
                    <SearchableSelect
                      value={String(form.shipmentLimitSecond || '172800')}
                      allowClear={false}
                      onChange={(v) => update('shipmentLimitSecond', v)}
                      options={SHIPMENT_LIMIT_OPTIONS.map((row) => ({
                        value: row.value,
                        label: t(`tiktokPublish.${row.labelKey}`),
                      }))}
                      placement="top"
                    />
                  </div>
                ) : null}
              </div>
            )}
            {/* 上架方式 / 预约上架 / 限购 / 预售：与仓·运费模板无关，故不放进 skipSiteWarehouseFreight 门控内 */}
            <div className="tko-wizard__form" onFocusCapture={() => setActiveSection('sales')}>
              <label className="tko-field" data-field-error="shelfWay">
                <span className="tko-field__label tko-field__label-with-help">
                  {t('tiktokPublish.shelfWay')}
                  <FieldHelpMark text={t('templateEditor.shelfWayHelpTpl')} />
                </span>
                <SearchableSelect
                  value={String(form.shelfWay || '1') === '2' ? '2' : '1'}
                  allowClear={false}
                  disabled={templateLoading || savingTemplate}
                  onChange={updateShelfWay}
                  options={[
                    { value: '1', label: t('tiktokPublish.shelfNow') },
                    { value: '2', label: t('tiktokPublish.shelfSchedule') },
                  ]}
                />
              </label>

              {String(form.shelfWay || '1') === '2' ? (
                <div className={`tko-field${fieldErrors.hopeOnSaleDate ? ' tko-field--invalid' : ''}`} data-field-error="hopeOnSaleDate">
                  <span className="tko-field__label tko-field__label-with-help">
                    {t('tiktokPublish.scheduleAt')}
                    <FieldHelpMark text={t('tiktokPublish.scheduleHelp')} />
                  </span>
                  <TiktokDateTimePicker
                    value={form.hopeOnSaleDate || ''}
                    onChange={updateScheduledSaleAt}
                    placeholder={t('tiktokPublish.pickSchedule')}
                    ariaLabel={t('tiktokPublish.scheduleAria')}
                    disabled={templateLoading || savingTemplate}
                    disablePast
                  />
                  <FieldErrorText message={fieldErrors.hopeOnSaleDate} />
                </div>
              ) : null}

              <label className={`tko-field${fieldErrors.maxOrderQuantity ? ' tko-field--invalid' : ''}`} data-field-error="maxOrderQuantity">
                <span className="tko-field__label">{t('tiktokPublish.maxOrder')}</span>
                <input
                  className="tko-input"
                  inputMode="numeric"
                  value={form.maxOrderQuantity || ''}
                  disabled={templateLoading || savingTemplate}
                  onChange={(e) => update('maxOrderQuantity', e.target.value.replace(/[^\d]/g, ''))}
                  placeholder={t('tiktokPublish.unlimited')}
                />
                <FieldErrorText message={fieldErrors.maxOrderQuantity} />
              </label>

              <label className={`tko-field${fieldErrors.cumulativeOrderQuantity ? ' tko-field--invalid' : ''}`} data-field-error="cumulativeOrderQuantity">
                <span className="tko-field__label">{t('tiktokPublish.cumulative')}</span>
                <input
                  className="tko-input"
                  inputMode="numeric"
                  value={form.cumulativeOrderQuantity || ''}
                  disabled={templateLoading || savingTemplate}
                  onChange={(e) => update('cumulativeOrderQuantity', e.target.value.replace(/[^\d]/g, ''))}
                  placeholder={t('tiktokPublish.unlimited')}
                />
                <FieldErrorText message={fieldErrors.cumulativeOrderQuantity} />
              </label>

              <p className="tko-wizard__inline-hint">
                {t('tiktokPublish.limitHint')}
              </p>

              {preSaleTypeOptions.length ? (
                <div className={`tko-field tko-field--full${fieldErrors.preSaleType ? ' tko-field--invalid' : ''}`} data-field-error="preSaleType">
                  <span className="tko-field__label tko-field__label-with-help">
                    {t('tiktokPublish.preSale')}
                    <FieldHelpMark text={t('tiktokPublish.preSaleHelp')} />
                  </span>
                  <SearchableSelect
                    value={form.preSaleType || ''}
                    allowClear
                    onChange={(v) => update('preSaleType', v || '')}
                    options={preSaleTypeOptions.map((type) => ({
                      value: type,
                      label: TIKTOK_PRESALE_TYPE_KEYS[type]
                        ? t(`tiktokPublish.${TIKTOK_PRESALE_TYPE_KEYS[type]}`)
                        : type,
                    }))}
                    placeholder={t('tiktokPublish.preSaleNone')}
                    placement="top"
                  />

                  {preSaleRule?.fulfillment === 'release_date' ? (
                    <label className={`tko-field tko-field--full${fieldErrors.preSaleReleaseDate ? ' tko-field--invalid' : ''}`} data-field-error="preSaleReleaseDate">
                      <span className="tko-field__label">{t('tiktokPublish.preSaleAt')}</span>
                      <input
                        type="datetime-local"
                        className="tko-input"
                        value={form.preSaleReleaseDate || ''}
                        disabled={templateLoading || savingTemplate}
                        onChange={(e) => update('preSaleReleaseDate', e.target.value)}
                      />
                      <FieldErrorText message={fieldErrors.preSaleReleaseDate} />
                    </label>
                  ) : null}

                  {preSaleRule?.fulfillment === 'handling_duration_days' ? (
                    <label className={`tko-field tko-field--full${fieldErrors.preSaleHandlingDays ? ' tko-field--invalid' : ''}`} data-field-error="preSaleHandlingDays">
                      <span className="tko-field__label">{t('tiktokPublish.preSaleDays')}</span>
                      <input
                        className="tko-input"
                        inputMode="numeric"
                        value={form.preSaleHandlingDays || ''}
                        disabled={templateLoading || savingTemplate}
                        onChange={(e) => update('preSaleHandlingDays', e.target.value.replace(/[^\d]/g, ''))}
                        placeholder={`${preSaleRule.minDays}–${preSaleRule.maxDays}`}
                      />
                      <FieldErrorText message={fieldErrors.preSaleHandlingDays} />
                    </label>
                  ) : null}

                  {preSaleRule ? (
                    <p className="tko-wizard__inline-hint">
                      {preSaleRule.fulfillment === 'release_date'
                        ? t('tiktokPublish.preSaleReleaseHint', { type: form.preSaleType, min: preSaleRule.minDays, max: preSaleRule.maxDays })
                        : t(
                          preSaleRule.unit === 'BUSINESS_DAY' ? 'tiktokPublish.preSaleHandlingBiz' : 'tiktokPublish.preSaleHandlingCal',
                          { type: form.preSaleType, min: preSaleRule.minDays, max: preSaleRule.maxDays },
                        )}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

        </div>

        <div className="tko-wizard__footer">
          <button
            type="button"
            className="tko-btn tko-btn--ghost"
            onClick={() => navigate(
              officialMode && afterSaveNavigate
                ? afterSaveNavigate
                : buildTiktokPath('templates/product'),
            )}
            disabled={savingTemplate || templateLoading}
          >
            {t('templateEditor.backList')}
          </button>
          <div className="tko-wizard__footer-right">
            {!officialMode ? (
              <button
                type="button"
                className="tko-btn tko-btn--secondary"
                onClick={onPublishFromTemplate}
                disabled={savingTemplate || templateLoading || !templateHydrated || !shopId}
              >
                {t('productTemplate.publish')}
              </button>
            ) : null}
            <button
              type="button"
              className="tko-btn tko-btn--primary"
              onClick={onSaveProductTemplate}
              disabled={savingTemplate || templateLoading || !templateHydrated || (!officialMode && !shopId)}
            >
              {savingTemplate ? t('tiktokPublish.saving') : t('shop.save')}
            </button>
          </div>
        </div>
        <PublishScrollJumpFab prefix="tko" />
      </section>

      <SaveAttributeTemplateMetaModal
        open={attrSaveModalOpen}
        shopId={shopId}
        officialMode={officialMode}
        shopType={shopType}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        productAttributes={form.productAttributes}
        attrDefs={allProductAttrs}
        onClose={() => setAttrSaveModalOpen(false)}
        onSaved={refreshAttrTemplates}
      />
    </div>
  );
}
